import React, { useState, useEffect, useRef } from 'react';
import { api } from '@/App';
import { getHutangPiutangAccess } from '@/lib/permissions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Plus, Trash2, History, Search, Check, X, Camera, GripVertical, ArrowUpDown, Pencil, FolderCog, ArrowLeftRight, RotateCcw } from 'lucide-react';
import { canManageFinance, isReadOnly } from '@/lib/permissions';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// 30-color palette for account name cell coloring (light + vivid tones)
const ACCOUNT_COLORS = [
  '#fecaca', '#fed7aa', '#fde68a', '#d9f99d', '#bbf7d0', '#a7f3d0', '#99f6e4', '#a5f3fc',
  '#bae6fd', '#bfdbfe', '#c7d2fe', '#ddd6fe', '#e9d5ff', '#f5d0fe', '#fbcfe8', '#fecdd3',
  '#ef4444', '#f97316', '#eab308', '#84cc16', '#22c55e', '#10b981', '#14b8a6', '#06b6d4',
  '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899'
];

const ColorPicker = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-1.5" data-testid="account-color-picker">
    <button
      type="button"
      onClick={() => onChange('')}
      title="Tidak ada warna"
      data-testid="color-none"
      className={`h-6 w-6 rounded border border-slate-300 flex items-center justify-center bg-white ${!value ? 'ring-2 ring-offset-1 ring-teal-500' : ''}`}
    >
      <span className="text-[10px] text-slate-400">✕</span>
    </button>
    {ACCOUNT_COLORS.map((c) => (
      <button
        key={c}
        type="button"
        onClick={() => onChange(c)}
        title={c}
        data-testid={`color-${c}`}
        className={`h-6 w-6 rounded border border-slate-300 ${value === c ? 'ring-2 ring-offset-1 ring-teal-500' : ''}`}
        style={{ backgroundColor: c }}
      />
    ))}
  </div>
);


const HutangPiutang = ({ user }) => {
  // Role-based access: which tables to show + whether transfer is allowed
  const hpAccess = getHutangPiutangAccess(user?.role);
  const showTable = (t) => hpAccess.tables === null || hpAccess.tables.includes(t);

  const [branches, setBranches] = useState([]);
  const [centralAccounts, setCentralAccounts] = useState([]);
  const [otherAccounts, setOtherAccounts] = useState([]);
  const [managerAccounts, setManagerAccounts] = useState([]);
  const [customerAccounts, setCustomerAccounts] = useState([]);
  const [pendingMoney, setPendingMoney] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [capturing, setCapturing] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const pageRef = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  
  // Toggle untuk show zero balances
  const [showZeroBalances, setShowZeroBalances] = useState(false);
  const [lastTxnDates, setLastTxnDates] = useState({});
  const [lastTxnSort, setLastTxnSort] = useState(null);
  const readOnly = isReadOnly(user);
  const LAYOUT_STORAGE_KEY = 'hutangPiutangLayout';
  const BASE_LAYOUT = [['central'], ['piutang', 'hutang_usaha'], ['branch'], ['dana_investasi', 'manager']];

  const normalizeLayout = (saved) => {
    const base = BASE_LAYOUT.map((c) => [...c]);
    if (!Array.isArray(saved) || saved.length !== 4) return base;
    const all = base.flat();
    const cleaned = saved.map((col) => (Array.isArray(col) ? col.filter((k) => all.includes(k)) : []));
    const present = cleaned.flat();
    all.forEach((k) => {
      if (!present.includes(k)) cleaned[0].push(k);
    });
    return cleaned;
  };

  const loadLayout = () => {
    try {
      return normalizeLayout(JSON.parse(localStorage.getItem(LAYOUT_STORAGE_KEY)));
    } catch {
      return BASE_LAYOUT.map((c) => [...c]);
    }
  };

  const [layout, setLayout] = useState(loadLayout);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  const [draggingKey, setDraggingKey] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  // Ambil susunan tabel global (berlaku untuk semua user)
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/settings/ui-layout');
        setLayout(normalizeLayout(res.data?.hutang_piutang_layout));
      } catch (e) {
        // fallback: pakai cache lokal
      } finally {
        setLayoutLoaded(true);
      }
    })();
  }, []);

  // Simpan susunan secara global (terlihat oleh semua user) + cache lokal
  useEffect(() => {
    if (!layoutLoaded) return;
    localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout));
    if (!readOnly) {
      api.put('/settings/ui-layout', { hutang_piutang_layout: layout }).catch(() => {});
    }
  }, [layout, layoutLoaded, readOnly]);

  
  // Search
  const [searchBranch, setSearchBranch] = useState('');
  const [searchLabaRugi, setSearchLabaRugi] = useState('');
  const [searchInvestment, setSearchInvestment] = useState('');
  
  // Delete Dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteData, setDeleteData] = useState({ type: '', id: '', name: '' });
  const [deletePassword, setDeletePassword] = useState('');
  
  // Central Account Dialog
  const [centralAccountDialog, setCentralAccountDialog] = useState(false);
  const [centralAccountForm, setCentralAccountForm] = useState({
    account_name: '',
    account_number: '',
    bank_name: '',
    initial_balance: 0,
    group_id: '',
    color: ''
  });
  const [editingCentralId, setEditingCentralId] = useState(null);

  // Central Account Groups
  const [centralGroups, setCentralGroups] = useState([]);
  const [groupDialog, setGroupDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [editingGroup, setEditingGroup] = useState(null); // {id, name}

  // Other Account Dialog
  const [otherAccountDialog, setOtherAccountDialog] = useState(false);
  const [otherAccountForm, setOtherAccountForm] = useState({
    account_name: '',
    account_number: '',
    bank_name: '',
    initial_balance: 0,
    color: ''
  });
  const [editingOtherId, setEditingOtherId] = useState(null);
  const [managerAccountDialog, setManagerAccountDialog] = useState(false);
  const [managerAccountForm, setManagerAccountForm] = useState({
    manager_id: '',
    account_name: '',
    account_number: '',
    bank_name: '',
    initial_balance: 0
  });
  const [editingManagerAccountId, setEditingManagerAccountId] = useState(null);
  const [customerAccountDialog, setCustomerAccountDialog] = useState(false);
  const [customerAccountForm, setCustomerAccountForm] = useState({
    customer_name: '',
    account_name: '',
    account_number: '',
    bank_name: '',
    initial_balance: 0,
    color: ''
  });
  const [editingCustomerAccountId, setEditingCustomerAccountId] = useState(null);

  // Pending Money Dialog
  const [pendingDialog, setPendingDialog] = useState(false);
  const [pendingForm, setPendingForm] = useState({
    amount: '',
    received_date: new Date().toISOString().split('T')[0],
    bank_name: '',
    account_number: '',
    notes: ''
  });

  // Confirm Pending Dialog
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [selectedPending, setSelectedPending] = useState(null);
  const [confirmForm, setConfirmForm] = useState({
    target_account_type: 'CENTRAL',
    target_account_id: ''
  });

  // Process Pending Dialog (Bulk Transfer)
  const [processPendingDialog, setProcessPendingDialog] = useState(false);
  const [selectedPendingIds, setSelectedPendingIds] = useState([]);
  const [processPendingForm, setProcessPendingForm] = useState({
    to_type: 'CENTRAL',
    to_account_id: '',
    description: ''
  });
  
  // State untuk edit keterangan di tabel Proses Dana Pending
  const [editingPendingNotes, setEditingPendingNotes] = useState({});

  // Add Pending Dialog (inside Process Dialog)
  const [addPendingDialog, setAddPendingDialog] = useState(false);
  const [addPendingForm, setAddPendingForm] = useState({
    tanggal: new Date().toISOString().split('T')[0],
    jenis_transaksi: '', // 'PENGIRIM' or 'PENERIMA'
    tipe_rekening: 'CENTRAL',
    rekening_id: '',
    jumlah: '',
    keterangan: ''
  });

  // Helper to determine if account type is Asset or Liability
  // Asset: CENTRAL, BRANCH, CUSTOMER (Piutang)
  // Liability: HUTANG_USAHA, MANAGER
  const isAssetAccountType = (type) => {
    return ['CENTRAL', 'BRANCH', 'CUSTOMER'].includes(type);
  };

  // Transfer Dialog
  const [transferDialog, setTransferDialog] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferForm, setTransferForm] = useState({
    from_type: 'CENTRAL',
    from_account_id: '',
    to_type: 'BRANCH',
    to_account_id: '',
    amount: '',
    description: '',
    transfer_date: new Date().toISOString().split('T')[0]
  });

  // Transaction History Dialog
  const [historyDialog, setHistoryDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [branchLabaRugi, setBranchLabaRugi] = useState(null);
  const [branchInvestasi, setBranchInvestasi] = useState(null);
  // History view mode: 'last10' (default) shows last 10 transactions; 'date' shows date range filter
  const [historyViewMode, setHistoryViewMode] = useState('last10');
  const [historyMonth, setHistoryMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [historyFilter, setHistoryFilter] = useState({
    startDate: '',
    endDate: '',
    limit: 10
  });
  // Display-only sort by "Tgl. Transaksi": null (ledger order) | 'asc' | 'desc'
  const [txDateSort, setTxDateSort] = useState(null);
  const [historyEditMode, setHistoryEditMode] = useState(false);
  const [editRows, setEditRows] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const canEditHistoryOrder = canManageFinance(user);
  const [transactionDetailDialog, setTransactionDetailDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [editingDescription, setEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState('');
  const dialogContentRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [branchesRes, centralRes, otherRes, managerRes, customerRes, managersRes, pendingRes, transfersRes, investmentsRes, groupsRes] = await Promise.all([
        api.get('/branches'),
        api.get('/central-accounts'),
        api.get('/other-accounts'),
        api.get('/manager-accounts'),
        api.get('/customer-accounts'),
        api.get('/managers'),
        api.get('/pending-money'),
        api.get('/transfers'),
        api.get('/investments'),
        api.get('/central-accounts/groups')
      ]);
      
      try {
        const lastTxnRes = await api.get('/account-transactions/last-dates');
        setLastTxnDates(lastTxnRes.data || {});
      } catch (lastTxnError) {
        console.error('Failed to fetch last transaction dates:', lastTxnError);
      }
      
      setBranches(branchesRes.data);
      setCentralAccounts(centralRes.data);
      setOtherAccounts(otherRes.data);
      setManagerAccounts(managerRes.data);
      setCustomerAccounts(customerRes.data);
      setManagers(managersRes.data);
      setPendingMoney(pendingRes.data);
      setTransfers(transfersRes.data);
      setInvestments(investmentsRes.data);
      setCentralGroups(groupsRes.data);
    } catch (error) {
      toast.error('Gagal mengambil data');
    } finally {
      setLoading(false);
    }
  };

  // Selisih hari dari tanggal transaksi terakhir sebuah rekening ke hari ini.
  const getDaysSinceLastTxn = (accountId) => {
    const dateStr = lastTxnDates[accountId];
    if (!dateStr) return null;
    const txDate = new Date(`${String(dateStr).slice(0, 10)}T00:00:00`);
    if (isNaN(txDate)) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.max(0, Math.round((today - txDate) / 86400000));
  };

  const getLastTxnColor = (days) => {
    if (days === null) return 'text-slate-400';
    if (days > 14) return 'bg-red-100 text-red-700 font-semibold';
    if (days > 7) return 'bg-yellow-100 text-yellow-800 font-semibold';
    return 'text-slate-600';
  };

  const formatCurrency = (amount) => {    const n = (amount === null || amount === undefined || isNaN(amount)) ? 0 : amount;
    const sign = n < 0 ? '-' : '';
    const num = new Intl.NumberFormat('id-ID', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.abs(Math.round(n)));
    // "Rp." + non-breaking space + number (dot after Rp, 1 space, never wraps)
    return `${sign}Rp.\u00a0${num}`;
  };

  // Central Account Functions
  const handleCreateCentralAccount = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (editingCentralId) {
        // Update existing account
        await api.put(`/central-accounts/${editingCentralId}`, {
          account_name: centralAccountForm.account_name,
          account_number: centralAccountForm.account_number,
          bank_name: centralAccountForm.bank_name,
          group_id: centralAccountForm.group_id || null,
          color: centralAccountForm.color || null,
          initial_balance: 0 // Always 0, don't allow editing
        });
        toast.success('Rekening pusat berhasil diperbarui');
      } else {
        // Create new account
        await api.post('/central-accounts', {
          ...centralAccountForm,
          group_id: centralAccountForm.group_id || null,
          color: centralAccountForm.color || null
        });
        toast.success('Rekening pusat berhasil ditambahkan');
      }
      setCentralAccountDialog(false);
      setCentralAccountForm({ account_name: '', account_number: '', bank_name: '', initial_balance: 0, group_id: '', color: '' });
      setEditingCentralId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan rekening');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCentralAccount = (account) => {
    setEditingCentralId(account.id);
    setCentralAccountForm({
      account_name: account.account_name,
      account_number: account.account_number || '',
      bank_name: account.bank_name || '',
      initial_balance: 0,
      group_id: account.group_id || '',
      color: account.color || ''
    });
    setCentralAccountDialog(true);
  };

  // Central Account Group handlers
  const handleCreateGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    try {
      await api.post('/central-accounts/groups', { name });
      toast.success('Grup berhasil ditambahkan');
      setNewGroupName('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menambah grup');
    }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup) return;
    const name = editingGroup.name.trim();
    if (!name) return;
    try {
      await api.put(`/central-accounts/groups/${editingGroup.id}`, { name });
      toast.success('Grup berhasil diperbarui');
      setEditingGroup(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal memperbarui grup');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Hapus grup ini? Rekening di dalamnya akan dipindah ke grup default "Rekening Pusat".')) return;
    try {
      await api.delete(`/central-accounts/groups/${groupId}`);
      toast.success('Grup berhasil dihapus');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal menghapus grup');
    }
  };

  // Reorder accounts within a single group (display-only ordering; merges subset back)
  const onCentralGroupDragEnd = (groupAccounts) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldI = groupAccounts.findIndex(i => i.id === active.id);
    const newI = groupAccounts.findIndex(i => i.id === over.id);
    if (oldI === -1 || newI === -1) return;
    const reordered = arrayMove(groupAccounts, oldI, newI);
    const orderMap = {};
    reordered.forEach((it, idx) => { orderMap[it.id] = idx + 1; });
    setCentralAccounts(prev => prev.map(a => (a.id in orderMap ? { ...a, display_order: orderMap[a.id] } : a)));
    const items = reordered.map((it, idx) => ({ id: it.id, display_order: idx + 1 }));
    api.put('/central-accounts/reorder', { items })
      .then(() => toast.success('Urutan berhasil diperbarui'))
      .catch(() => { toast.error('Gagal memperbarui urutan'); fetchData(); });
  };

  // Build the grouped structure for Rekening Pusat: default group first, then custom groups in order
  const getCentralGroupSections = () => {
    const sortAcc = (arr) => [...arr].sort((a, b) =>
      (a.display_order == null) - (b.display_order == null) || (a.display_order || 0) - (b.display_order || 0)
    );
    const sections = [];
    // Default (ungrouped) group
    const ungrouped = centralAccounts.filter(a => !a.group_id);
    sections.push({ id: null, name: 'Rekening Pusat', accounts: sortAcc(ungrouped) });
    // Custom groups (already ordered by backend)
    for (const g of centralGroups) {
      sections.push({ id: g.id, name: g.name, accounts: sortAcc(centralAccounts.filter(a => a.group_id === g.id)) });
    }
    return sections;
  };

  const handleDeleteCentralAccount = async (accountId) => {
    if (!confirm('Yakin ingin menghapus rekening ini?')) return;
    try {
      await api.delete(`/central-accounts/${accountId}`);
      toast.success('Rekening berhasil dihapus');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus rekening');
    }
  };

  // Other Account Functions
  const handleCreateOtherAccount = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (editingOtherId) {
        // Update existing account
        await api.put(`/other-accounts/${editingOtherId}`, {
          account_name: otherAccountForm.account_name,
          account_number: otherAccountForm.account_number,
          bank_name: otherAccountForm.bank_name,
          color: otherAccountForm.color || null,
          initial_balance: 0 // Always 0, don't allow editing
        });
        toast.success('Hutang Usaha berhasil diperbarui');
      } else {
        // Create new account
        await api.post('/other-accounts', { ...otherAccountForm, color: otherAccountForm.color || null });
        toast.success('Hutang Usaha berhasil ditambahkan');
      }
      setOtherAccountDialog(false);
      setOtherAccountForm({ account_name: '', account_number: '', bank_name: '', initial_balance: 0, color: '' });
      setEditingOtherId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan rekening');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditOtherAccount = (account) => {
    setEditingOtherId(account.id);
    setOtherAccountForm({
      account_name: account.account_name,
      account_number: account.account_number || '',
      bank_name: account.bank_name || '',
      initial_balance: 0,
      color: account.color || ''
    });
    setOtherAccountDialog(true);
  };

  // Manager Account Functions
  const handleCreateManagerAccount = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (editingManagerAccountId) {
        await api.put(`/manager-accounts/${editingManagerAccountId}`, {
          manager_id: managerAccountForm.manager_id,
          account_name: managerAccountForm.account_name,
          account_number: managerAccountForm.account_number,
          bank_name: managerAccountForm.bank_name,
          initial_balance: 0
        });
        toast.success('Rekening mitra berhasil diperbarui');
      } else {
        await api.post('/manager-accounts', managerAccountForm);
        toast.success('Rekening mitra berhasil ditambahkan');
      }
      setManagerAccountDialog(false);
      setManagerAccountForm({ manager_id: '', account_name: '', account_number: '', bank_name: '', initial_balance: 0 });
      setEditingManagerAccountId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan rekening');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditManagerAccount = (account) => {
    setEditingManagerAccountId(account.id);
    setManagerAccountForm({
      manager_id: account.manager_id,
      account_name: account.account_name,
      account_number: account.account_number || '',
      bank_name: account.bank_name || '',
      initial_balance: 0
    });
    setManagerAccountDialog(true);
  };

  // Customer Account Functions
  const handleCreateCustomerAccount = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (editingCustomerAccountId) {
        await api.put(`/customer-accounts/${editingCustomerAccountId}`, {
          customer_name: customerAccountForm.customer_name,
          account_name: customerAccountForm.account_name,
          account_number: customerAccountForm.account_number,
          bank_name: customerAccountForm.bank_name,
          color: customerAccountForm.color || null,
          initial_balance: 0
        });
        toast.success('Rekening customer berhasil diperbarui');
      } else {
        await api.post('/customer-accounts', { ...customerAccountForm, color: customerAccountForm.color || null });
        toast.success('Rekening customer berhasil ditambahkan');
      }
      setCustomerAccountDialog(false);
      setCustomerAccountForm({ customer_name: '', account_name: '', account_number: '', bank_name: '', initial_balance: 0, color: '' });
      setEditingCustomerAccountId(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan rekening');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditCustomerAccount = (account) => {
    setEditingCustomerAccountId(account.id);
    setCustomerAccountForm({
      customer_name: account.customer_name,
      account_name: account.account_name,
      account_number: account.account_number || '',
      bank_name: account.bank_name || '',
      initial_balance: 0,
      color: account.color || ''
    });
    setCustomerAccountDialog(true);
  };

  // Delete Account Functions
  const handleDeleteClick = (type, id, name) => {
    setDeleteData({ type, id, name });
    setDeletePassword('');
    setDeleteDialog(true);
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    if (!deletePassword) {
      toast.error('Password harus diisi');
      return;
    }

    try {
      let endpoint = '';
      if (deleteData.type === 'CENTRAL') {
        endpoint = `/central-accounts/${deleteData.id}`;
      } else if (deleteData.type === 'HUTANG_USAHA') {
        endpoint = `/other-accounts/${deleteData.id}`;
      } else if (deleteData.type === 'PIUTANG') {
        endpoint = `/customer-accounts/${deleteData.id}`;
      }

      await api.delete(endpoint, {
        data: { password: deletePassword }
      });

      toast.success('Rekening berhasil dihapus');
      setDeleteDialog(false);
      setDeletePassword('');
      setDeleteData({ type: '', id: '', name: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus rekening');
    }
  };

  // Pending Money Functions
  const handleCreatePendingMoney = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        ...pendingForm,
        amount: parseFloat(pendingForm.amount),
        received_date: new Date(pendingForm.received_date).toISOString()
      };
      await api.post('/pending-money', payload);
      toast.success('Uang pending berhasil ditambahkan');
      setPendingDialog(false);
      setPendingForm({ amount: '', received_date: new Date().toISOString().split('T')[0], bank_name: '', account_number: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambahkan dana pending');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmPending = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.patch(`/pending-money/${selectedPending.id}/confirm?target_account_type=${confirmForm.target_account_type}&target_account_id=${confirmForm.target_account_id}`);
      toast.success('Uang pending berhasil dikonfirmasi');
      setConfirmDialog(false);
      setSelectedPending(null);
      setConfirmForm({ target_account_type: 'CENTRAL', target_account_id: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal konfirmasi');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectPending = async (id) => {
    if (!confirm('Yakin ingin menolak dana pending ini?')) return;
    try {
      await api.patch(`/pending-money/${id}/reject`);
      toast.success('Uang pending ditolak');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menolak');
    }
  };

  const handleDeletePending = async (id) => {
    if (!confirm('Yakin ingin menghapus dana pending ini?')) return;
    try {
      await api.delete(`/pending-money/${id}`);
      toast.success('Uang pending dihapus');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus');
    }
  };

  // Format number with thousand separator
  const formatNumberInput = (value) => {
    // Remove non-numeric characters except for the value
    const numericValue = value.replace(/[^0-9]/g, '');
    // Format with thousand separator
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  // Parse formatted number back to number
  const parseFormattedNumber = (value) => {
    if (!value) return 0;
    return parseFloat(value.toString().replace(/\./g, '')) || 0;
  };

  // Handle jumlah input change with formatting
  const handleJumlahChange = (e) => {
    const rawValue = e.target.value.replace(/\./g, '');
    // Prevent negative values
    if (rawValue.startsWith('-')) return;
    const formattedValue = formatNumberInput(rawValue);
    setAddPendingForm({...addPendingForm, jumlah: formattedValue});
  };

  // Open Process Pending Dialog (Bulk)
  const handleOpenProcessPending = () => {
    setSelectedPendingIds([]);
    setProcessPendingForm({
      to_type: 'CENTRAL',
      to_account_id: '',
      description: ''
    });
    setProcessPendingDialog(true);
  };

  // Open Add Pending Dialog
  const handleOpenAddPending = () => {
    setAddPendingForm({
      tanggal: new Date().toISOString().split('T')[0],
      pengirim_penerima: '',
      tipe_rekening: 'CENTRAL',
      rekening_id: '',
      jumlah: '',
      keterangan: ''
    });
    setAddPendingDialog(true);
  };

  // Handle Add Pending Submit
  const handleAddPendingSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!addPendingForm.jenis_transaksi) {
      toast.error('Pilih jenis transaksi (Pengirim/Penerima)');
      return;
    }
    if (!addPendingForm.rekening_id) {
      toast.error('Pilih rekening');
      return;
    }
    if (!addPendingForm.keterangan.trim()) {
      toast.error('Keterangan wajib diisi');
      return;
    }
    const amount = parseFormattedNumber(addPendingForm.jumlah);
    if (amount <= 0) {
      toast.error('Jumlah harus lebih dari 0');
      return;
    }

    if (submitting) return;
    setSubmitting(true);
    try {
      // Get account name for display
      const accounts = getAccountsByType(addPendingForm.tipe_rekening, true);
      const selectedAccount = accounts.find(acc => acc.id === addPendingForm.rekening_id);
      const accountName = selectedAccount ? (selectedAccount.displayName || selectedAccount.account_name) : addPendingForm.tipe_rekening;
      
      // Determine the effect on balance based on jenis_transaksi and account type
      // PENERIMA: Asset (+), Liability (-)
      // PENGIRIM: Asset (-), Liability (+)
      const isAsset = isAssetAccountType(addPendingForm.tipe_rekening);
      const isPenerima = addPendingForm.jenis_transaksi === 'PENERIMA';
      
      // Calculate effective amount (positive = increase balance, negative = decrease balance)
      let effectiveAmount = amount;
      if (isPenerima) {
        // Penerima: Asset increases (+), Liability decreases (-)
        effectiveAmount = isAsset ? amount : -amount;
      } else {
        // Pengirim: Asset decreases (-), Liability increases (+)
        effectiveAmount = isAsset ? -amount : amount;
      }

      // Create description with direction info
      const directionText = isPenerima ? 'Penerima' : 'Pengirim';
      const effectText = effectiveAmount >= 0 ? 'menambah' : 'mengurangi';
      
      const payload = {
        amount: Math.abs(amount), // Always store positive amount
        received_date: new Date(addPendingForm.tanggal).toISOString(),
        bank_name: '', // Not used anymore
        account_number: '', // Not used anymore
        notes: addPendingForm.keterangan.trim(), // Use user's description directly
        // Additional metadata for processing
        jenis_transaksi: addPendingForm.jenis_transaksi,
        tipe_rekening: addPendingForm.tipe_rekening,
        rekening_id: addPendingForm.rekening_id,
        rekening_name: accountName, // Send account name explicitly
        effective_amount: effectiveAmount
      };

      await api.post('/pending-money', payload);
      
      const actionText = effectiveAmount >= 0 ? 'menambah' : 'mengurangi';
      toast.success(`Dana pending berhasil ditambahkan. Akan ${actionText} saldo ${accountName} sebesar Rp.\u00a0${amount.toLocaleString('id-ID')}`);
      
      setAddPendingDialog(false);
      setAddPendingForm({
        tanggal: new Date().toISOString().split('T')[0],
        jenis_transaksi: '',
        tipe_rekening: 'CENTRAL',
        rekening_id: '',
        jumlah: '',
        keterangan: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambahkan dana pending');
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle checkbox for pending item
  const handleTogglePendingItem = (id) => {
    setSelectedPendingIds(prev => 
      prev.includes(id) 
        ? prev.filter(pid => pid !== id)
        : [...prev, id]
    );
  };

  // Select all pending items
  const handleSelectAllPending = () => {
    const pendingItems = pendingMoney.filter(p => p.status === 'PENDING');
    if (selectedPendingIds.length === pendingItems.length) {
      setSelectedPendingIds([]);
    } else {
      setSelectedPendingIds(pendingItems.map(p => p.id));
    }
  };

  // Handle update keterangan di tabel Proses Dana Pending
  const handleUpdatePendingNotes = async (moneyId, newNotes) => {
    try {
      await api.patch(`/pending-money/${moneyId}`, { notes: newNotes });
      // Update local state
      setPendingMoney(prev => prev.map(p => 
        p.id === moneyId ? { ...p, notes: newNotes } : p
      ));
      // Clear editing state
      setEditingPendingNotes(prev => {
        const newState = { ...prev };
        delete newState[moneyId];
        return newState;
      });
      toast.success('Keterangan berhasil diupdate');
    } catch (error) {
      console.error('Failed to update notes:', error);
      toast.error('Gagal mengupdate keterangan');
    }
  };

  // Calculate total selected amount
  const getSelectedTotalAmount = () => {
    return pendingMoney
      .filter(p => selectedPendingIds.includes(p.id))
      .reduce((sum, p) => {
        // PENERIMA = minus (dana pending berkurang), PENGIRIM = plus (dana pending bertambah)
        const effectiveAmount = p.jenis_transaksi === 'PENERIMA' ? -(p.amount || 0) : (p.amount || 0);
        return sum + effectiveAmount;
      }, 0);
  };


  // Handle Process Pending (Bulk Transfer)
  const handleProcessPending = async () => {
    if (submitting) return;
    try {
      if (selectedPendingIds.length === 0) {
        toast.error('Pilih minimal 1 dana pending');
        return;
      }
      if (!processPendingForm.to_account_id) {
        toast.error('Pilih rekening tujuan');
        return;
      }

      setSubmitting(true);
      const totalAmount = getSelectedTotalAmount();
      
      // Mark all selected pending as confirmed (this will add to target account balance)
      let processedCount = 0;
      for (const pendingId of selectedPendingIds) {
        try {
          await api.patch(`/pending-money/${pendingId}/confirm?target_account_type=${processPendingForm.to_type}&target_account_id=${processPendingForm.to_account_id}`);
          processedCount++;
        } catch (err) {
          console.error(`Failed to process pending ${pendingId}:`, err);
        }
      }
      
      toast.success(`${processedCount} dana pending berhasil diproses ke rekening tujuan (Total: Rp.\u00a0${totalAmount.toLocaleString('id-ID')})`);
      setProcessPendingDialog(false);
      setSelectedPendingIds([]);
      setProcessPendingForm({
        to_type: 'CENTRAL',
        to_account_id: '',
        description: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memproses dana pending');
    } finally {
      setSubmitting(false);
    }
  };

  // Transfer Functions
  const handleCreateTransfer = async (e) => {
    e.preventDefault();
    if (transferLoading) return; // Prevent double submission
    
    setTransferLoading(true);
    try {
      // Generate idempotency key — same key on a retry means backend will return the
      // existing transfer instead of executing the deduction twice.
      const clientRequestId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const payload = {
        ...transferForm,
        amount: parseFloat(transferForm.amount),
        transfer_date: new Date(transferForm.transfer_date).toISOString(),
        client_request_id: clientRequestId
      };
      await api.post('/transfers', payload);
      toast.success('Transfer berhasil');
      setTransferDialog(false);
      setTransferForm({
        from_type: 'CENTRAL',
        from_account_id: '',
        to_type: 'BRANCH',
        to_account_id: '',
        amount: '',
        description: '',
        transfer_date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal melakukan transfer');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleViewHistory = async (accountType, accountId, accountName) => {
    // Open dialog immediately with loading state
    setSelectedAccount({ type: accountType, id: accountId, name: accountName });
    setTransactions([]); // Clear old transactions
    setBranchLabaRugi(null); // Reset Laba Rugi Mitra
    setBranchInvestasi(null); // Reset Saldo Investasi Mitra
    // For a branch account, show the matching "Laba Rugi Mitra" balance (already loaded on this page)
    if (accountType === 'BRANCH') {
      const mitraManager = managers.find(m =>
        (m.accounts || []).some(a => (a.account_name || '') === accountName)
      );
      const mitraAccount = mitraManager
        ? (mitraManager.accounts || []).find(a => (a.account_name || '') === accountName)
        : null;
      setBranchLabaRugi(mitraAccount ? (mitraAccount.balance || 0) : null);
      // Saldo Investasi Mitra (Dana Investasi) - only relevant if the mitra actually has one
      const investasi = mitraManager ? (mitraManager.total_investment_amount || 0) : 0;
      setBranchInvestasi(investasi > 0 ? investasi : null);
    }
    setHistoryViewMode('last10'); // Default to Last 10
    setHistoryFilter({ startDate: '', endDate: '', limit: 10 });
    setTxDateSort(null); // Reset display sort to ledger order
    setLoadingHistory(true); // Set loading state
    setHistoryDialog(true); // Open dialog immediately
    
    // Then fetch data
    try {
      const res = await api.get(`/account-transactions?account_id=${accountId}`);
      setTransactions(res.data);
    } catch (error) {
      toast.error('Gagal mengambil history transaksi');
      setHistoryDialog(false); // Close on error
    } finally {
      setLoadingHistory(false); // Stop loading
    }
  };

  // 6 bulan terakhir untuk filter "Nama Bulan" pada history transaksi.
  const getLast6Months = () => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      return {
        value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
        label: d.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' }),
      };
    });
  };

  const getFilteredTransactions = () => {
    let filtered = [...transactions];

    // Sort by date (oldest first - ascending)
    filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    // "Last 10" mode: show only the 10 most recent transactions
    if (historyViewMode === 'last10') {
      return filtered.slice(-10);
    }

    // "Nama Bulan" mode: semua transaksi pada bulan terpilih,
    // berdasarkan TANGGAL TRANSAKSI (bukan tanggal input).
    if (historyViewMode === 'month') {
      return filtered.filter((tx) => {
        const raw = tx.transaction_date || tx.created_at;
        return raw && String(raw).slice(0, 7) === historyMonth;
      });
    }

    // "Tanggal" (date range) mode: apply date filter
    if (historyFilter.startDate) {
      filtered = filtered.filter(tx =>
        new Date(tx.created_at) >= new Date(historyFilter.startDate)
      );
    }
    if (historyFilter.endDate) {
      filtered = filtered.filter(tx =>
        new Date(tx.created_at) <= new Date(historyFilter.endDate + 'T23:59:59')
      );
    }

    return filtered;
  };

  // Display-only ordering for the history table. Keeps getFilteredTransactions()
  // (ledger/created_at order) intact so "Sisa Saldo" stays correct; only the
  // rendered rows are re-sorted by Tgl. Transaksi AND the running "Saldo" column
  // is recomputed to follow that display order. Does NOT touch the database.
  const getDisplayTransactions = () => {
    const base = getFilteredTransactions();
    if (!txDateSort) {
      // Ledger order: use the stored balance snapshots as-is.
      return base.map((tx) => ({ ...tx, _displayBalance: tx.balance_after }));
    }
    const signed = (tx) =>
      tx.transaction_type === 'DEBIT' ? Math.abs(tx.amount) : -Math.abs(tx.amount);
    // Opening balance = balance right before the first row of the displayed window
    // (base is created_at-ascending). Endpoint stays equal to the ledger balance.
    const opening = base.length > 0 ? base[0].balance_after - signed(base[0]) : 0;
    // Recompute a chronological (by transaction_date) running balance...
    const chrono = [...base].sort((a, b) => {
      const da = new Date(a.transaction_date || a.created_at);
      const db = new Date(b.transaction_date || b.created_at);
      return da - db;
    });
    let running = opening;
    const withBalance = chrono.map((tx) => {
      running += signed(tx);
      return { ...tx, _displayBalance: running };
    });
    // ...then present ascending or descending as chosen.
    return txDateSort === 'asc' ? withBalance : withBalance.reverse();
  };

  const toggleTxDateSort = () => {
    setTxDateSort((prev) => (prev === 'asc' ? null : 'asc'));
  };

  // --- Edit mode: susun ulang record dengan drag & drop ---
  const enterHistoryEditMode = () => {
    setTxDateSort(null);
    setEditRows(getFilteredTransactions().map((tx) => ({ ...tx })));
    setHistoryEditMode(true);
  };

  const exitHistoryEditMode = () => {
    setHistoryEditMode(false);
    setEditRows([]);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleRowDrop = (targetIndex) => {
    setDragOverIndex(null);
    if (dragIndex === null || dragIndex === targetIndex) return;
    const next = [...editRows];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(targetIndex, 0, moved);
    setEditRows(next);
    setDragIndex(null);
  };

  const editRowsWithBalance = () => {
    const signed = (tx) => (tx.transaction_type === 'DEBIT' ? Math.abs(tx.amount) : -Math.abs(tx.amount));
    const base = getFilteredTransactions();
    let running = base.length > 0 ? base[0].balance_after - signed(base[0]) : 0;
    return editRows.map((tx) => {
      running += signed(tx);
      return { ...tx, _displayBalance: running };
    });
  };

  const handleSaveHistoryOrder = async () => {
    setSavingOrder(true);
    try {
      const res = await api.post('/account-transactions/reorder', {
        account_id: selectedAccount?.id,
        ordered_ids: editRows.map((tx) => tx.id),
      });
      const refreshed = await api.get(`/account-transactions?account_id=${selectedAccount?.id}`);
      setTransactions(refreshed.data);
      exitHistoryEditMode();
      toast.success(`Urutan disimpan. Saldo akhir: ${formatCurrency(res.data.final_balance)}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menyimpan urutan');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleTransactionDetailClick = async (transaction) => {
    // If it's a transfer transaction, prefer the Pengirim/Penerima stored directly
    // on the transaction record (saved at creation time). Fall back to matching
    // against /transfers for legacy records created before this data was persisted.
    if (transaction.reference_type === 'TRANSFER_IN' || transaction.reference_type === 'TRANSFER_OUT') {
      if (transaction.from_account_name && transaction.to_account_name) {
        setSelectedTransaction({
          ...transaction,
          transferDetails: {
            pengirim_type: transaction.from_account_type || '',
            pengirim_name: transaction.from_account_name,
            penerima_type: transaction.to_account_type || '',
            penerima_name: transaction.to_account_name,
            from_type: transaction.from_account_type || '',
            from_account_name: transaction.from_account_name,
            to_type: transaction.to_account_type || '',
            to_account_name: transaction.to_account_name
          }
        });
      } else {
        try {
          // Legacy fallback: fetch all transfers to find matching one by date and amount
          const res = await api.get('/transfers');
          const matchingTransfer = res.data.find(t => {
            const transferDate = new Date(t.transfer_date).toDateString();
            const txDate = new Date(transaction.created_at).toDateString();
            return transferDate === txDate && t.amount === transaction.amount;
          });
          if (matchingTransfer) {
            setSelectedTransaction({ ...transaction, transferDetails: matchingTransfer });
          } else {
            setSelectedTransaction(transaction);
          }
        } catch (error) {
          setSelectedTransaction(transaction);
        }
      }
    } else if ((transaction.reference_type === 'PENDING_CONFIRMED' || transaction.reference_type === 'PENDING_MONEY') && transaction.from_account_name && transaction.to_account_name) {
      // For PENDING_CONFIRMED or PENDING_MONEY with transfer details
      // Pengirim = account from which money is sent (from_account)
      // Penerima = account to which money goes (to_account)
      setSelectedTransaction({
        ...transaction,
        transferDetails: {
          pengirim_type: transaction.from_account_type || '',
          pengirim_name: transaction.from_account_name,
          penerima_type: transaction.to_account_type || '',
          penerima_name: transaction.to_account_name,
          // Keep old fields for backwards compatibility
          from_type: transaction.from_account_type || '',
          from_account_name: transaction.from_account_name,
          to_type: transaction.to_account_type || '',
          to_account_name: transaction.to_account_name
        }
      });
    } else {
      setSelectedTransaction(transaction);
    }
    setEditedDescription(transaction.description || '');
    setEditingDescription(false);
    setTransactionDetailDialog(true);
  };

  // Auto-scroll to bottom when transaction detail dialog opens
  useEffect(() => {
    if (transactionDetailDialog) {
      // Simple approach: scroll to bottom of page after short delay
      const scrollTimer = setTimeout(() => {
        window.scrollTo({
          top: document.documentElement.scrollHeight,
          behavior: 'smooth'
        });
      }, 100);
      
      return () => clearTimeout(scrollTimer);
    }
  }, [transactionDetailDialog]);

  const handleUpdateDescription = async () => {
    try {
      await api.put(`/account-transactions/${selectedTransaction.id}`, {
        description: editedDescription
      });
      
      // Update local state
      setTransactions(transactions.map(tx => 
        tx.id === selectedTransaction.id 
          ? { ...tx, description: editedDescription }
          : tx
      ));
      setSelectedTransaction({ ...selectedTransaction, description: editedDescription });
      setEditingDescription(false);
      toast.success('Keterangan berhasil diperbarui');
    } catch (error) {
      toast.error('Gagal memperbarui keterangan');
    }
  };

  // Get account options for transfer/confirm
  // Helper function to sort branches by status: ACTIVE → INACTIVE → BLACKLIST
  const getSortedBranches = (branchesList) => {
    const statusOrder = { 'ACTIVE': 1, 'INACTIVE': 2, 'BLACKLIST': 3 };
    return [...branchesList].sort((a, b) => {
      const aStatus = a.status || 'ACTIVE';
      const bStatus = b.status || 'ACTIVE';
      const aOrder = statusOrder[aStatus] || 4;
      const bOrder = statusOrder[bStatus] || 4;
      
      // Primary sort: by status
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      
      // Secondary sort: by order_number within same status group
      const aOrderNum = a.order_number || 9999;
      const bOrderNum = b.order_number || 9999;
      
      if (aOrderNum !== bOrderNum) {
        return aOrderNum - bOrderNum;
      }
      
      // Tertiary sort: by name alphabetically
      return (a.name || '').localeCompare(b.name || '');
    });
  };

  const getAccountsByType = (type, includeOther = false) => {
    if (type === 'CENTRAL') {
      return centralAccounts;
    } else if ((type === 'OTHER' || type === 'HUTANG_USAHA') && includeOther) {
      return otherAccounts;
    } else if (type === 'BRANCH') {
      const allBranchAccounts = [];
      // Sort branches first: ACTIVE → INACTIVE → BLACKLIST
      getSortedBranches(branches).forEach(branch => {
        if (branch.accounts) {
          branch.accounts.forEach(acc => {
            allBranchAccounts.push({
              ...acc,
              displayName: `${branch.name} - ${acc.account_name || acc.name}`
            });
          });
        }
      });
      return allBranchAccounts;
    } else if (type === 'MANAGER') {
      const allManagerAccounts = [];
      managers.forEach(manager => {
        if (manager.accounts && manager.accounts.length > 0) {
          manager.accounts.forEach((acc, idx) => {
            allManagerAccounts.push({
              ...acc,
              id: acc.id || `${manager.id}_${idx}`,
              manager_name: manager.name,
              name: acc.account_name,
              displayName: `${manager.name} - ${acc.account_name}`
            });
          });
        }
      });
      return allManagerAccounts;
    } else if (type === 'CUSTOMER') {
      return customerAccounts.map(acc => ({
        ...acc,
        name: acc.account_name
      }));
    }
    return [];
  };

  // Helper for thousand separator in input
  const formatNumber = (value) => {
    if (!value && value !== 0) return '';
    const numericValue = parseFloat(value.toString().replace(/[^\d]/g, ''));
    if (isNaN(numericValue)) return '';
    return new Intl.NumberFormat('id-ID').format(numericValue);
  };

  const handleNumberChange = (value, setter, field) => {
    const numericValue = value.replace(/[^\d]/g, '');
    setter(prev => ({ 
      ...prev, 
      [field]: numericValue === '' ? 0 : parseFloat(numericValue) 
    }));
  };

  // Filter branches by search
  const filteredBranches = getSortedBranches(
    branches.filter(branch =>
      branch.name.toLowerCase().includes(searchBranch.toLowerCase())
    )
  );

  // Filter managers by search for Laba Rugi Mitra
  const filteredManagers = managers.filter(manager => {
    const managerName = manager.name.toLowerCase();
    const searchTerm = searchLabaRugi.toLowerCase();
    return managerName.includes(searchTerm) || 
           (manager.accounts && manager.accounts.some(acc => 
             acc.account_name.toLowerCase().includes(searchTerm)
           ));
  });

  // Filter investments by search for Dana Investasi
  const filteredInvestments = investments.filter(investment => {
    const searchTerm = searchInvestment.toLowerCase();
    return investment.manager_name.toLowerCase().includes(searchTerm) || 
           investment.branch_name.toLowerCase().includes(searchTerm);
  });

  // Group investments by manager for display
  const investmentsByManager = filteredInvestments.reduce((acc, inv) => {
    if (!acc[inv.manager_id]) {
      acc[inv.manager_id] = {
        manager_id: inv.manager_id,
        manager_name: inv.manager_name,
        total_investment: inv.investment_amount || 0, // Take from first record (same for all records of this manager)
        investments: []
      };
    }
    acc[inv.manager_id].investments.push(inv);
    return acc;
  }, {});
  const investmentGroups = Object.values(investmentsByManager).sort((a, b) => {
    const orderA = managers.find(m => m.id === a.manager_id)?.order_number ?? 9999;
    const orderB = managers.find(m => m.id === b.manager_id)?.order_number ?? 9999;
    return (orderA - orderB) || String(a.manager_name).localeCompare(String(b.manager_name));
  });

  const totalCentralBalance = centralAccounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);
  const totalOtherBalance = otherAccounts.reduce((sum, acc) => sum + (acc.balance || 0), 0);
  const totalBranchBalance = branches.reduce((sum, branch) => {
    const branchTotal = (branch.accounts || []).reduce((s, acc) => s + (acc.balance || 0), 0);
    return sum + branchTotal;
  }, 0);
  const totalPendingAmount = pendingMoney.filter(p => p.status === 'PENDING').reduce((sum, p) => {
    // PENERIMA = minus (dana pending berkurang), PENGIRIM = plus (dana pending bertambah)
    const effectiveAmount = p.jenis_transaksi === 'PENERIMA' ? -(p.amount || 0) : (p.amount || 0);
    return sum + effectiveAmount;
  }, 0);

  if (loading) return <div>Loading...</div>;

  const captureFullPage = async () => {
    if (!pageRef.current) return;
    setCapturing(true);
    toast.info('Mengambil gambar halaman...');
    await new Promise(resolve => setTimeout(resolve, 150));
    try {
      const { toBlob } = await import('html-to-image');
      const node = pageRef.current;
      const blob = await toBlob(node, {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        cacheBust: true,
        width: node.scrollWidth,
        height: node.scrollHeight,
        style: { transform: 'none' },
        filter: (el) => !(el?.dataset && el.dataset.html2canvasIgnore === 'true'),
      });
      if (!blob) {
        toast.error('Gagal membuat gambar');
        setCapturing(false);
        return;
      }
      if (navigator.clipboard && window.ClipboardItem) {
        try {
          await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
          toast.success('Halaman berhasil disalin ke clipboard!');
          setCapturing(false);
          return;
        } catch (err) {
          console.error('Error copying to clipboard:', err);
        }
      }
      // Fallback: download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Hutang-Piutang-${new Date().toISOString().slice(0, 10)}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Gambar berhasil diunduh!');
      setCapturing(false);
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      toast.error('Gagal menangkap halaman');
      setCapturing(false);
    }
  };

  // Drag-drop sortable row (render-prop keeps table cell layout intact)
  const SortableRow = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
    return children({ setNodeRef, style, attributes, listeners });
  };

  // Per-group DnD wrapper (renders no DOM of its own, safe inside a table body)
  const DndContextGroup = ({ sensors, onDragEnd, ids, children }) => (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );

  const persistReorder = async (reordered, setItems, endpoint) => {
    setItems(reordered);
    const items = reordered.map((it, idx) => ({ id: it.id, display_order: idx + 1 }));
    try {
      await api.put(endpoint, { items });
      toast.success('Urutan berhasil diperbarui');
    } catch (e) {
      toast.error('Gagal memperbarui urutan');
      fetchData();
    }
  };

  const onDragEndFor = (items, setItems, endpoint) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldI = items.findIndex(i => i.id === active.id);
    const newI = items.findIndex(i => i.id === over.id);
    if (oldI === -1 || newI === -1) return;
    persistReorder(arrayMove(items, oldI, newI), setItems, endpoint);
  };

  const CARD_KEYS = ['central', 'piutang', 'hutang_usaha', 'branch', 'dana_investasi', 'manager'];
  const DEFAULT_LAYOUT = [['central'], ['piutang', 'hutang_usaha'], ['branch'], ['dana_investasi', 'manager']];
  const cardVisible = (key) => (key === 'branch' ? true : showTable(key));

  const dragHandle = (key) => (readOnly ? null : (
    <span
      draggable
      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', key); setDraggingKey(key); }}
      onDragEnd={() => { setDraggingKey(null); setDragOverCol(null); }}
      className="text-slate-400 hover:text-teal-600 cursor-grab active:cursor-grabbing"
      title="Geser untuk memindahkan tabel ke kolom lain"
      data-testid={`hp-card-handle-${key}`}
    >
      <GripVertical className="h-4 w-4" />
    </span>
  ));

  const handleCardDrop = (targetCol, beforeKey, keyFromEvent) => {
    const moving = draggingKey || keyFromEvent;
    if (!moving) return;
    setLayout((prev) => {
      const next = prev.map((col) => col.filter((k) => k !== moving));
      const col = next[targetCol];
      const at = beforeKey && beforeKey !== moving ? col.indexOf(beforeKey) : -1;
      if (at >= 0) col.splice(at, 0, moving);
      else col.push(moving);
      return next;
    });
    setDraggingKey(null);
    setDragOverCol(null);
  };

  const renderLayoutColumn = (colIndex) => (
    <div
      key={`hp-col-${colIndex}`}
      className={`space-y-4 min-h-[80px] rounded-lg transition-colors ${dragOverCol === colIndex ? 'bg-teal-50 ring-2 ring-teal-300' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setDragOverCol(colIndex); }}
      onDragLeave={() => setDragOverCol((c) => (c === colIndex ? null : c))}
      onDrop={(e) => { e.preventDefault(); handleCardDrop(colIndex, null, e.dataTransfer.getData('text/plain')); }}
      data-testid={`hp-column-${colIndex}`}
    >
      {(layout[colIndex] || []).filter(cardVisible).map((key) => (
        <div
          key={key}
          onDragOver={(e) => { e.preventDefault(); setDragOverCol(colIndex); }}
          onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleCardDrop(colIndex, key, e.dataTransfer.getData('text/plain')); }}
          className={draggingKey === key ? 'opacity-50' : ''}
          data-testid={`hp-card-${key}`}
        >
          {cardRenderers[key]}
        </div>
      ))}
    </div>
  );

  const cardRenderers = {
    central: (
      <>
              {/* Rekening Pusat */}
              <Card className="border-green-200" style={{ display: showTable('central') ? undefined : 'none' }}>
                <CardHeader className="flex flex-row items-center justify-between py-3 bg-green-50">
                  <CardTitle className="text-base text-green-800 flex items-center gap-2">{dragHandle('central')}Rekening Pusat</CardTitle>
                  <div className="flex gap-2">
                  <Dialog open={groupDialog} onOpenChange={(open) => { setGroupDialog(open); if (!open) { setNewGroupName(''); setEditingGroup(null); } }}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" className="border-green-300 text-green-800" title="Kelola Grup" aria-label="Kelola Grup" data-testid="manage-groups-btn">
                        <FolderCog className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Kelola Grup Rekening Pusat</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <Input
                            placeholder="Nama grup baru"
                            value={newGroupName}
                            onChange={(e) => setNewGroupName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCreateGroup(); } }}
                            data-testid="new-group-name-input"
                          />
                          <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={handleCreateGroup} data-testid="add-group-btn">
                            <Plus className="h-3 w-3 mr-1" />Tambah
                          </Button>
                        </div>
                        <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                          {centralGroups.length === 0 && (
                            <p className="text-xs text-slate-400 italic text-center py-2">Belum ada grup. Rekening tanpa grup masuk ke "Rekening Pusat".</p>
                          )}
                          {centralGroups.map((g) => (
                            <div key={g.id} className="flex items-center gap-2" data-testid={`group-row-${g.id}`}>
                              {editingGroup && editingGroup.id === g.id ? (
                                <>
                                  <Input
                                    value={editingGroup.name}
                                    onChange={(e) => setEditingGroup({ ...editingGroup, name: e.target.value })}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleUpdateGroup(); } }}
                                    className="h-8"
                                  />
                                  <Button size="sm" className="h-8 bg-teal-600 hover:bg-teal-700" onClick={handleUpdateGroup}>Simpan</Button>
                                  <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingGroup(null)}>Batal</Button>
                                </>
                              ) : (
                                <>
                                  <span className="flex-1 text-sm font-medium">{g.name}</span>
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditingGroup({ id: g.id, name: g.name })} data-testid={`edit-group-${g.id}`}>
                                    Edit
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => handleDeleteGroup(g.id)} data-testid={`delete-group-${g.id}`}>
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Dialog open={centralAccountDialog} onOpenChange={(open) => {
                    setCentralAccountDialog(open);
                    if (!open) {
                      setEditingCentralId(null);
                      setCentralAccountForm({ account_name: '', account_number: '', bank_name: '', initial_balance: 0, group_id: '', color: '' });
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-teal-600 hover:bg-teal-700 px-2" title="Tambah" aria-label="Tambah">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{editingCentralId ? 'Edit Rekening Pusat' : 'Tambah Rekening Pusat'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateCentralAccount} className="space-y-3">
                        <div>
                          <Label className="text-sm">Nama Rekening *</Label>
                          <Input required value={centralAccountForm.account_name} onChange={(e) => setCentralAccountForm({...centralAccountForm, account_name: e.target.value})} />
                        </div>
                        <div>
                          <Label className="text-sm">Nomor Rekening</Label>
                          <Input value={centralAccountForm.account_number} onChange={(e) => setCentralAccountForm({...centralAccountForm, account_number: e.target.value})} />
                        </div>
                        <div>
                          <Label className="text-sm">Nama Bank</Label>
                          <Input value={centralAccountForm.bank_name} onChange={(e) => setCentralAccountForm({...centralAccountForm, bank_name: e.target.value})} />
                        </div>
                        <div>
                          <Label className="text-sm">Grup</Label>
                          <Select
                            value={centralAccountForm.group_id || 'none'}
                            onValueChange={(val) => setCentralAccountForm({...centralAccountForm, group_id: val === 'none' ? '' : val})}
                          >
                            <SelectTrigger data-testid="central-account-group-select">
                              <SelectValue placeholder="Pilih grup" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Rekening Pusat (default)</SelectItem>
                              {centralGroups.map((g) => (
                                <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-sm">Warna</Label>
                          <ColorPicker value={centralAccountForm.color} onChange={(c) => setCentralAccountForm({...centralAccountForm, color: c})} />
                        </div>
                        <div>
                          <Label className="text-sm">Saldo Awal (Rp)</Label>
                          <Input 
                            type="text" 
                            value="0" 
                            disabled
                            className="bg-slate-100 cursor-not-allowed"
                          />
                          <p className="text-xs text-slate-500 mt-1">Saldo awal selalu 0</p>
                        </div>
                        <DialogFooter>
                          <Button type="button" variant="outline" size="sm" onClick={() => setCentralAccountDialog(false)}>Batal</Button>
                          <Button type="submit" size="sm" disabled={submitting} className="bg-teal-600 hover:bg-teal-700" data-testid="central-account-submit-btn">{submitting ? 'Menyimpan...' : 'Simpan'}</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="py-2 text-xs w-10">No.</TableHead>
                        <TableHead className="py-2 text-xs">Nama</TableHead>
                        <TableHead className="py-2 text-xs">Saldo</TableHead>
                        <TableHead className="py-2 text-xs">Bank</TableHead>
                        <TableHead className="py-2 text-xs">No. Rek</TableHead>
                        {showZeroBalances && <TableHead className="text-center py-2 text-xs">Aksi</TableHead>}
                      </TableRow>
                    </TableHeader>
                    {getCentralGroupSections().map((section) => {
                      const visible = section.accounts.filter(a => showZeroBalances || a.balance !== 0);
                      // Hide a non-default, empty group entirely when zero balances are hidden
                      if (section.id !== null && visible.length === 0 && !showZeroBalances) return null;
                      const subtotal = section.accounts.reduce((s, a) => s + (a.balance || 0), 0);
                      const colTotal = showZeroBalances ? 3 : 2;
                      const hasGroups = centralGroups.length > 0;
                      return (
                        <TableBody key={section.id || 'default'} data-testid={`central-group-${section.id || 'default'}`}>
                          {/* Group title row (only when custom groups exist) */}
                          {hasGroups && (
                          <TableRow className="bg-green-100/70">
                            <TableCell colSpan={showZeroBalances ? 6 : 5} className="py-1.5 text-xs font-bold text-green-900 uppercase tracking-wide">
                              {section.name}
                            </TableCell>
                          </TableRow>
                          )}
                          <DndContextGroup
                            sensors={sensors}
                            onDragEnd={onCentralGroupDragEnd(visible)}
                            ids={visible.map(a => a.id)}
                          >
                            {visible.map((account, idx) => (
                              <SortableRow key={account.id} id={account.id}>
                                {({ setNodeRef, style, attributes, listeners }) => (
                                  <TableRow ref={setNodeRef} style={style}>
                                    <TableCell className="py-1 text-xs text-slate-500">
                                      {reorderMode ? (
                                        <span {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing inline-flex" data-testid="drag-handle-central"><GripVertical size={14} className="text-slate-400" /></span>
                                      ) : (idx + 1)}
                                    </TableCell>
                                    <TableCell
                                      className="py-1 text-xs cursor-pointer hover:bg-blue-50"
                                      style={account.color ? { backgroundColor: account.color } : undefined}
                                      onClick={() => handleEditCentralAccount(account)}
                                    >
                                      <div className="font-medium hover:text-teal-600">{account.account_name}</div>
                                    </TableCell>
                                    <TableCell
                                      className="py-1 whitespace-nowrap cursor-pointer hover:bg-blue-50"
                                      onClick={() => handleViewHistory('CENTRAL', account.id, account.account_name)}
                                    >
                                      <span className={`text-xs font-semibold hover:underline ${account.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                        {formatCurrency(account.balance)}
                                      </span>
                                    </TableCell>
                                    <TableCell className="py-1 text-xs text-black">
                                      {account.bank_name || '-'}
                                    </TableCell>
                                    <TableCell className="py-1 text-xs text-black">
                                      {account.account_number || '-'}
                                    </TableCell>
                                    {showZeroBalances && (
                                      <TableCell className="text-center py-1">
                                        {account.balance === 0 && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                            onClick={() => handleDeleteClick('CENTRAL', account.id, account.account_name)}
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </Button>
                                        )}
                                      </TableCell>
                                    )}
                                  </TableRow>
                                )}
                              </SortableRow>
                            ))}
                          </DndContextGroup>
                          {visible.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={showZeroBalances ? 6 : 5} className="text-center text-slate-400 italic py-2 text-xs">
                                Tidak ada rekening
                              </TableCell>
                            </TableRow>
                          )}
                          {/* Group subtotal (green "Total") — only when custom groups exist */}
                          {hasGroups && (
                          <TableRow className="bg-green-50 border-t border-green-300">
                            <TableCell colSpan={2} className="py-1 text-xs font-semibold text-green-900 whitespace-nowrap">Total</TableCell>
                            <TableCell className="py-1 text-xs font-semibold text-green-900 whitespace-nowrap" data-testid={`central-subtotal-${section.id || 'default'}`}>
                              {formatCurrency(subtotal)}
                            </TableCell>
                            <TableCell colSpan={colTotal}></TableCell>
                          </TableRow>
                          )}
                          {/* Separator line between group tables (only when custom groups exist) */}
                          {hasGroups && (
                          <TableRow>
                            <TableCell colSpan={showZeroBalances ? 6 : 5} className="p-0 border-0 bg-transparent">
                              <div className="h-8 flex items-center">
                                <div className="w-full border-t-2 border-gray-300"></div>
                              </div>
                            </TableCell>
                          </TableRow>
                          )}
                        </TableBody>
                      );
                    })}
                    {/* Final total row in a footer so it gets the thick blue top line like other tables */}
                    <TableFooter>
                      <TableRow className="bg-blue-100 border-t-2 border-blue-600">
                        <TableCell colSpan={2} className="py-1 text-xs font-bold text-blue-900 whitespace-nowrap" data-testid="central-total-label">
                          {centralGroups.length > 0 ? 'TOTAL KESELURUHAN' : 'TOTAL REKENING PUSAT'}
                        </TableCell>
                        <TableCell className="py-1 text-xs font-bold text-blue-900 whitespace-nowrap" data-testid="central-grand-total">
                          {formatCurrency(centralAccounts.reduce((sum, acc) => sum + (acc.balance || 0), 0))}
                        </TableCell>
                        <TableCell colSpan={showZeroBalances ? 3 : 2}></TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </CardContent>
              </Card>
      </>
    ),
    piutang: (
      <>
              {/* Piutang */}
              <Card className="border-green-200" style={{ display: showTable('piutang') ? undefined : 'none' }}>
                <CardHeader className="py-3 bg-green-50">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-base text-green-800 flex items-center gap-2">{dragHandle('piutang')}Piutang</CardTitle>
                    <Dialog open={customerAccountDialog} onOpenChange={(open) => {
                      setCustomerAccountDialog(open);
                      if (!open) {
                        setEditingCustomerAccountId(null);
                        setCustomerAccountForm({ customer_name: '', account_name: '', account_number: '', bank_name: '', initial_balance: 0, color: '' });
                      }
                    }}>
                      <DialogTrigger asChild>
                        <Button size="sm" className="bg-teal-600 hover:bg-teal-700 px-2" title="Tambah" aria-label="Tambah">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{editingCustomerAccountId ? 'Edit Piutang' : 'Tambah Piutang'}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleCreateCustomerAccount} className="space-y-3">
                          <div>
                            <Label className="text-sm">Nama Customer *</Label>
                            <Input required value={customerAccountForm.customer_name} onChange={(e) => setCustomerAccountForm({...customerAccountForm, customer_name: e.target.value, account_name: e.target.value})} />
                          </div>
                          <div>
                            <Label className="text-sm">Warna</Label>
                            <ColorPicker value={customerAccountForm.color} onChange={(c) => setCustomerAccountForm({...customerAccountForm, color: c})} />
                          </div>
                          <div>
                            <Label className="text-sm">Saldo Awal (Rp)</Label>
                            <Input 
                              type="text" 
                              value="0" 
                              disabled
                              className="bg-slate-100 cursor-not-allowed"
                            />
                            <p className="text-xs text-slate-500 mt-1">Saldo awal selalu 0</p>
                          </div>
                          <DialogFooter>
                            <Button type="button" variant="outline" size="sm" onClick={() => setCustomerAccountDialog(false)}>Batal</Button>
                            <Button type="submit" size="sm" disabled={submitting} className="bg-teal-600 hover:bg-teal-700" data-testid="customer-account-submit-btn">{submitting ? 'Menyimpan...' : 'Simpan'}</Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndFor(customerAccounts, setCustomerAccounts, '/customer-accounts/reorder')}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="py-2 text-xs w-10">No.</TableHead>
                        <TableHead className="py-2 text-xs">Nama</TableHead>
                        <TableHead className="py-2 text-xs">Saldo</TableHead>
                        {showZeroBalances && <TableHead className="text-center py-2 text-xs">Aksi</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <SortableContext items={customerAccounts.filter(account => showZeroBalances || account.balance !== 0).map(a => a.id)} strategy={verticalListSortingStrategy}>
                      {customerAccounts
                        .filter(account => showZeroBalances || account.balance !== 0)
                        .map((account, idx) => (
                        <SortableRow key={account.id} id={account.id}>
                          {({ setNodeRef, style, attributes, listeners }) => (
                        <TableRow ref={setNodeRef} style={style}>
                          <TableCell className="py-1 text-xs text-slate-500">
                            {reorderMode ? (
                              <span {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing inline-flex" data-testid="drag-handle-customer"><GripVertical size={14} className="text-slate-400" /></span>
                            ) : (idx + 1)}
                          </TableCell>
                          <TableCell
                            className="py-1 text-xs font-medium cursor-pointer hover:bg-blue-50"
                            style={account.color ? { backgroundColor: account.color } : undefined}
                            onClick={() => handleEditCustomerAccount(account)}
                          >
                            <span className="hover:text-teal-600">{account.customer_name}</span>
                          </TableCell>
                          <TableCell 
                            className="py-1 whitespace-nowrap cursor-pointer hover:bg-blue-50"
                            onClick={() => handleViewHistory('CUSTOMER', account.id, account.customer_name)}
                          >
                            <span className={`text-xs font-semibold hover:underline ${account.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(account.balance || 0)}
                            </span>
                          </TableCell>
                          {showZeroBalances && (
                            <TableCell className="text-center py-1">
                              {account.balance === 0 && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteClick('PIUTANG', account.id, account.customer_name)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                          )}
                        </SortableRow>
                      ))}
                      </SortableContext>
                      {customerAccounts.filter(account => showZeroBalances || account.balance !== 0).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={showZeroBalances ? 4 : 3} className="text-center text-slate-400 italic py-4 text-sm">
                            {showZeroBalances ? 'Belum ada piutang' : 'Tidak ada rekening dengan saldo'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                    <TableFooter>
                      <TableRow className="bg-blue-100 border-t-2 border-blue-600">
                        <TableCell colSpan={2} className="py-1 text-xs font-bold text-blue-900 whitespace-nowrap">TOTAL PIUTANG</TableCell>
                        <TableCell className="py-1 text-xs font-bold text-blue-900 whitespace-nowrap">
                          {formatCurrency(customerAccounts.filter(a => showZeroBalances || a.balance !== 0).reduce((sum, acc) => sum + (acc.balance || 0), 0))}
                        </TableCell>
                        {showZeroBalances && <TableCell></TableCell>}
                      </TableRow>
                    </TableFooter>
                  </Table>
                  </DndContext>
                </CardContent>
              </Card>
      </>
    ),
    hutang_usaha: (
      <>
              {/* Hutang Usaha */}
              <Card className="border-red-200" style={{ display: showTable('hutang_usaha') ? undefined : 'none' }}>
                <CardHeader className="flex flex-row items-center justify-between py-3 bg-red-50">
                  <CardTitle className="text-base text-red-800 flex items-center gap-2">{dragHandle('hutang_usaha')}Hutang Usaha</CardTitle>
                  <Dialog open={otherAccountDialog} onOpenChange={(open) => {
                    setOtherAccountDialog(open);
                    if (!open) {
                      setEditingOtherId(null);
                      setOtherAccountForm({ account_name: '', account_number: '', bank_name: '', initial_balance: 0, color: '' });
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="bg-teal-600 hover:bg-teal-700 px-2" title="Tambah" aria-label="Tambah">
                        <Plus className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>{editingOtherId ? 'Edit Hutang Usaha' : 'Tambah Hutang Usaha'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateOtherAccount} className="space-y-3">
                        <div>
                          <Label className="text-sm">Nama Rekening *</Label>
                          <Input required value={otherAccountForm.account_name} onChange={(e) => setOtherAccountForm({...otherAccountForm, account_name: e.target.value})} />
                        </div>
                        <div>
                          <Label className="text-sm">Nomor Rekening</Label>
                          <Input value={otherAccountForm.account_number} onChange={(e) => setOtherAccountForm({...otherAccountForm, account_number: e.target.value})} />
                        </div>
                        <div>
                          <Label className="text-sm">Nama Bank</Label>
                          <Input value={otherAccountForm.bank_name} onChange={(e) => setOtherAccountForm({...otherAccountForm, bank_name: e.target.value})} />
                        </div>
                        <div>
                          <Label className="text-sm">Warna</Label>
                          <ColorPicker value={otherAccountForm.color} onChange={(c) => setOtherAccountForm({...otherAccountForm, color: c})} />
                        </div>
                        <div>
                          <Label className="text-sm">Saldo Awal (Rp)</Label>
                          <Input 
                            type="text" 
                            value="0" 
                            disabled
                            className="bg-slate-100 cursor-not-allowed"
                          />
                          <p className="text-xs text-slate-500 mt-1">Saldo awal selalu 0</p>
                        </div>
                        <DialogFooter>
                          <Button type="button" variant="outline" size="sm" onClick={() => setOtherAccountDialog(false)}>Batal</Button>
                          <Button type="submit" size="sm" disabled={submitting} className="bg-teal-600 hover:bg-teal-700" data-testid="other-account-submit-btn">{submitting ? 'Menyimpan...' : 'Simpan'}</Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndFor(otherAccounts, setOtherAccounts, '/other-accounts/reorder')}>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="py-2 text-xs w-10">No.</TableHead>
                        <TableHead className="py-2 text-xs">Nama</TableHead>
                        <TableHead className="py-2 text-xs">Saldo</TableHead>
                        {showZeroBalances && <TableHead className="text-center py-2 text-xs">Aksi</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <SortableContext items={otherAccounts.filter(account => showZeroBalances || account.balance !== 0).map(a => a.id)} strategy={verticalListSortingStrategy}>
                      {otherAccounts
                        .filter(account => showZeroBalances || account.balance !== 0)
                        .map((account, idx) => (
                        <SortableRow key={account.id} id={account.id}>
                          {({ setNodeRef, style, attributes, listeners }) => (
                        <TableRow ref={setNodeRef} style={style}>
                          <TableCell className="py-1 text-xs text-slate-500">
                            {reorderMode ? (
                              <span {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing inline-flex" data-testid="drag-handle-other"><GripVertical size={14} className="text-slate-400" /></span>
                            ) : (idx + 1)}
                          </TableCell>
                          <TableCell 
                            className="py-1 text-xs cursor-pointer hover:bg-blue-50"
                            style={account.color ? { backgroundColor: account.color } : undefined}
                            onClick={() => handleEditOtherAccount(account)}
                          >
                            <div className="font-medium hover:text-teal-600">{account.account_name}</div>
                          </TableCell>
                          <TableCell 
                            className="py-1 whitespace-nowrap cursor-pointer hover:bg-blue-50"
                            onClick={() => handleViewHistory('HUTANG_USAHA', account.id, account.account_name)}
                          >
                            <span className={`text-xs font-semibold hover:underline ${account.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(account.balance)}
                            </span>
                          </TableCell>
                          {showZeroBalances && (
                            <TableCell className="text-center py-1">
                              {account.balance === 0 && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteClick('HUTANG_USAHA', account.id, account.account_name)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                          )}
                        </SortableRow>
                      ))}
                      </SortableContext>
                      {otherAccounts.filter(account => showZeroBalances || account.balance !== 0).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={showZeroBalances ? 4 : 3} className="text-center text-slate-400 italic py-4 text-xs">
                            {showZeroBalances ? 'Belum ada Hutang Usaha' : 'Tidak ada rekening dengan saldo'}
                          </TableCell>
                        </TableRow>
                      )}
                      {/* Total Row */}
                    </TableBody>
                    <TableFooter>
                      <TableRow className="bg-blue-100 border-t-2 border-blue-600">
                        <TableCell colSpan={2} className="py-1 text-xs font-bold text-blue-900 whitespace-nowrap">TOTAL HUTANG USAHA</TableCell>
                        <TableCell className="py-1 text-xs font-bold text-blue-900 whitespace-nowrap">
                          {formatCurrency(otherAccounts.filter(a => showZeroBalances || a.balance !== 0).reduce((sum, acc) => sum + (acc.balance || 0), 0))}
                        </TableCell>
                        {showZeroBalances && <TableCell></TableCell>}
                      </TableRow>
                    </TableFooter>
                  </Table>
                  </DndContext>
                </CardContent>
              </Card>
      </>
    ),
    dana_pending: (
      <>
              {/* Dana Pending (spans column 1 & 2) */}
              <Card className="border-green-200" style={{ display: showTable('dana_pending') ? undefined : 'none' }}>
                <CardHeader className="py-3 bg-green-50">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-base text-green-800">Dana Pending</CardTitle>
                    <Button
                      size="sm"
                      className="h-7 px-3 text-xs bg-teal-600 hover:bg-teal-700"
                      onClick={handleOpenProcessPending}
                    >
                      Proses
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="py-2 text-xs">Tanggal</TableHead>
                        <TableHead className="py-2 text-xs">Keterangan</TableHead>
                        <TableHead className="py-2 text-xs">Pengirim</TableHead>
                        <TableHead className="py-2 text-xs">Penerima</TableHead>
                        <TableHead className="text-right py-2 text-xs">Jumlah</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingMoney
                        .filter(money => money.status === 'PENDING')
                        .map((money) => (
                        <TableRow key={money.id}>
                          <TableCell className="py-2 text-xs">
                            {new Date(money.received_date).toLocaleDateString('id-ID')}
                          </TableCell>
                          <TableCell className="py-2 text-xs">
                            {money.notes || '-'}
                          </TableCell>
                          <TableCell className="py-2 text-xs">
                            {money.jenis_transaksi === 'PENGIRIM' ? (money.rekening_name || '-') : '-'}
                          </TableCell>
                          <TableCell className="py-2 text-xs">
                            {money.jenis_transaksi === 'PENERIMA' ? (money.rekening_name || '-') : '-'}
                          </TableCell>
                          <TableCell className={`text-right py-2 text-sm font-semibold ${money.jenis_transaksi === 'PENERIMA' ? 'text-red-600' : 'text-blue-700'}`}>
                            {money.jenis_transaksi === 'PENERIMA' ? `-${formatCurrency(money.amount)}` : formatCurrency(money.amount)}
                          </TableCell>
                        </TableRow>
                      ))}
                      {pendingMoney.filter(money => money.status === 'PENDING').length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center text-slate-400 italic py-4 text-sm">
                            Tidak ada dana pending
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                    {pendingMoney.filter(money => money.status === 'PENDING').length > 0 && (
                      <TableFooter>
                        <TableRow className="bg-blue-100 border-t-2 border-blue-600">
                          <TableCell colSpan={4} className="py-1 text-xs font-bold text-blue-900 whitespace-nowrap">TOTAL DANA PENDING</TableCell>
                          <TableCell className="text-right py-1 text-xs font-bold text-blue-900">
                            {formatCurrency(pendingMoney.filter(money => money.status === 'PENDING').reduce((sum, m) => {
                              // PENERIMA = minus (dana pending berkurang), PENGIRIM = plus (dana pending bertambah)
                              const effectiveAmount = m.jenis_transaksi === 'PENERIMA' ? -(m.amount || 0) : (m.amount || 0);
                              return sum + effectiveAmount;
                            }, 0))}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </CardContent>
              </Card>
      </>
    ),
    branch: (
      <>
              {/* Rekening Cabang with Search */}
              <Card className="border-green-200">
                <CardHeader className="py-3 bg-green-50">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-base text-green-800 flex items-center gap-2">{dragHandle('branch')}Saldo Rekening Cabang</CardTitle>
                    <div className="relative w-48">
                      <Search className="absolute left-2 top-2.5 h-3 w-3 text-slate-400" />
                      <Input
                    
                        value={searchBranch}
                        onChange={(e) => setSearchBranch(e.target.value)}
                        className="pl-7 h-8 text-xs"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="py-2 px-1 text-xs w-8">No.</TableHead>
                        <TableHead className="py-2 px-1 text-xs">Rekening</TableHead>
                        <TableHead className="py-2 px-1 text-xs">Saldo</TableHead>
                        <TableHead
                          className="py-2 px-1 w-12 text-xs text-center cursor-pointer select-none hover:bg-green-100"
                          onClick={() => setLastTxnSort((prev) => (prev === 'desc' ? null : 'desc'))}
                          data-testid="branch-last-txn-sort"
                          title="Klik untuk menampilkan rekening paling lama tidak bertransaksi di atas"
                        >
                          <span className="inline-flex items-center gap-1">
                            Last Txn
                            <ArrowUpDown className="h-3 w-3" />
                            {lastTxnSort === 'desc' && <span className="text-teal-600">▼</span>}
                          </span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const rows = [];
                        filteredBranches.forEach((branch) => {
                          (branch.accounts || [])
                            .filter(account => showZeroBalances || account.balance !== 0)
                            .forEach((account, idx) => {
                              rows.push({ branch, account, idx, days: getDaysSinceLastTxn(account.id) });
                            });
                        });
                        if (lastTxnSort === 'desc') {
                          // Paling lama tidak bertransaksi di atas; rekening tanpa transaksi di bawah.
                          rows.sort((a, b) => (b.days ?? -1) - (a.days ?? -1));
                        }
                        return rows.map(({ branch, account, idx, days: daysSinceTxn }, rowIndex) => {
                          const branchRowNo = rowIndex + 1;
                          return (
                            <TableRow key={`${branch.id}-${account.id}-${idx}`}>
                              <TableCell className="py-1 px-1 text-xs text-slate-500">{branchRowNo}</TableCell>
                              <TableCell className="py-1 px-1 text-xs cursor-pointer hover:bg-blue-50" style={account.color ? { backgroundColor: account.color } : undefined}>
                                <div className="font-medium hover:text-teal-600">{account.account_name || account.name || '-'}</div>
                              </TableCell>
                              <TableCell 
                                className="py-1 px-1 whitespace-nowrap cursor-pointer hover:bg-blue-50"
                                onClick={() => handleViewHistory('BRANCH', account.id, account.account_name || account.name)}
                              >
                                <span className={`text-xs font-semibold hover:underline ${account.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatCurrency(account.balance || 0)}
                                </span>
                              </TableCell>
                              <TableCell
                                className={`py-1 px-1 w-12 text-xs text-center whitespace-nowrap ${getLastTxnColor(daysSinceTxn)}`}
                                data-testid={`branch-last-txn-${account.id}`}
                                title={lastTxnDates[account.id] ? `Transaksi terakhir: ${lastTxnDates[account.id]}` : 'Belum ada transaksi'}
                              >
                                {daysSinceTxn === null ? '-' : daysSinceTxn}
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })()}
                      {filteredBranches.every(b => !b.accounts || b.accounts.length === 0 || b.accounts.every(a => !showZeroBalances && a.balance === 0)) && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-slate-400 italic py-4 text-xs">
                            {showZeroBalances ? 'Belum ada rekening cabang' : 'Tidak ada rekening dengan saldo'}
                          </TableCell>
                        </TableRow>
                      )}
                      {/* Total Row */}
                    </TableBody>
                    <TableFooter>
                      <TableRow className="bg-blue-100 border-t-2 border-blue-600">
                        <TableCell colSpan={2} className="py-1 px-1 text-xs font-bold text-blue-900 whitespace-nowrap">TOTAL REKENING CABANG</TableCell>
                        <TableCell className="py-1 px-1 text-xs font-bold text-blue-900 whitespace-nowrap">
                          {formatCurrency(filteredBranches.reduce((sum, branch) => {
                            const branchTotal = (branch.accounts || []).reduce((s, acc) => s + (acc.balance || 0), 0);
                            return sum + branchTotal;
                          }, 0))}
                        </TableCell>
                        <TableCell className="py-1" />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </CardContent>
              </Card>
      </>
    ),
    dana_investasi: (
      <>
              {/* Dana Investasi - Moved above Laba Rugi Mitra */}
              <Card className="border-red-200" style={{ display: showTable('dana_investasi') ? undefined : 'none' }}>
                <CardHeader className="py-3 bg-red-50">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-base text-red-800 flex items-center gap-2">{dragHandle('dana_investasi')}Dana Investasi</CardTitle>
                    <div className="relative w-48">
                      <Search className="absolute left-2 top-2.5 h-3 w-3 text-slate-400" />
                      <Input
                        value={searchInvestment}
                        onChange={(e) => setSearchInvestment(e.target.value)}
                        className="pl-7 h-8 text-xs"
                        placeholder="Cari mitra..."
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="py-2 text-xs w-10">No.</TableHead>
                        <TableHead className="py-2 text-xs">Mitra</TableHead>
                        <TableHead className="py-2 text-xs">Total Investasi</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {investmentGroups.length > 0 ? (
                        investmentGroups.map((group, idx) => (
                          <TableRow key={group.manager_id}>
                            <TableCell className="py-1 text-xs text-slate-500">{idx + 1}</TableCell>
                            <TableCell className="py-1 text-xs font-medium">
                              {group.manager_name}
                            </TableCell>
                            <TableCell className="py-1">
                              <span className={`text-xs font-semibold ${group.total_investment >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {formatCurrency(group.total_investment)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-slate-400 italic py-4 text-sm">
                            {searchInvestment ? 'Tidak ada hasil pencarian' : 'Belum ada Dana Investasi'}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                    {investmentGroups.length > 0 && (
                      <TableFooter>
                        <TableRow className="bg-blue-100 border-t-2 border-blue-600">
                          <TableCell colSpan={2} className="py-1 text-xs font-bold text-blue-900 whitespace-nowrap">TOTAL DANA INVESTASI</TableCell>
                          <TableCell className="py-1 text-xs font-bold text-blue-900 whitespace-nowrap">
                            {formatCurrency(investmentGroups.reduce((sum, g) => sum + g.total_investment, 0))}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </CardContent>
              </Card>
      </>
    ),
    manager: (
      <>
              {/* Laba Rugi Mitra */}
              <Card className="border-red-200" style={{ display: showTable('manager') ? undefined : 'none' }}>
                <CardHeader className="py-3 bg-red-50">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-base text-red-800 flex items-center gap-2">{dragHandle('manager')}Laba Rugi Mitra</CardTitle>
                    <div className="relative w-48">
                      <Search className="absolute left-2 top-2.5 h-3 w-3 text-slate-400" />
                      <Input
                        value={searchLabaRugi}
                        onChange={(e) => setSearchLabaRugi(e.target.value)}
                        className="pl-7 h-8 text-xs"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="py-2 text-xs w-10">No.</TableHead>
                        <TableHead className="py-2 text-xs">Rekening</TableHead>
                        <TableHead className="py-2 text-xs">Saldo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(() => {
                        const rows = filteredManagers
                          .filter(m => m.accounts && m.accounts.length > 0)
                          .flatMap(manager => (manager.accounts || [])
                            .filter(account => showZeroBalances || account.balance !== 0)
                            .map((account, idx) => ({ manager, account, accountId: account.id || `${manager.id}_${idx}` }))
                          )
                          .sort((a, b) => (b.account.balance || 0) - (a.account.balance || 0));
                        return rows.map((row, i) => (
                            <TableRow key={`${row.manager.id}-${i}`}>
                              <TableCell className="py-1 text-xs text-slate-500">{i + 1}</TableCell>
                              <TableCell className="py-1 text-xs font-medium">
                                {row.account.account_name}
                              </TableCell>
                              <TableCell 
                                className="py-1 whitespace-nowrap cursor-pointer hover:bg-blue-50"
                                onClick={() => handleViewHistory('MANAGER', row.accountId, row.account.account_name)}
                              >
                                <span className={`text-xs font-semibold hover:underline ${(row.account.balance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatCurrency(row.account.balance || 0)}
                                </span>
                              </TableCell>
                            </TableRow>
                          ));
                      })()}
                      {filteredManagers.filter(m => m.accounts && m.accounts.length > 0 && m.accounts.some(a => showZeroBalances || a.balance !== 0)).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-slate-400 italic py-4 text-sm">
                            {searchLabaRugi ? 'Tidak ada hasil pencarian' : (showZeroBalances ? 'Belum ada Laba Rugi Mitra' : 'Tidak ada rekening dengan saldo')}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                    {filteredManagers.filter(m => m.accounts && m.accounts.length > 0 && m.accounts.some(a => showZeroBalances || a.balance !== 0)).length > 0 && (
                      <TableFooter>
                        <TableRow className="bg-green-100 border-t border-green-600">
                          <TableCell colSpan={2} className="py-1 text-xs font-bold text-green-900">TOTAL LABA (&gt; 0)</TableCell>
                          <TableCell className="py-1 text-xs font-bold text-green-700" data-testid="total-laba-mitra">
                            {formatCurrency(filteredManagers.flatMap(m => m.accounts || []).filter(a => (a.balance || 0) > 0).reduce((sum, a) => sum + (a.balance || 0), 0))}
                          </TableCell>
                        </TableRow>
                        <TableRow className="bg-red-100 border-t border-red-600">
                          <TableCell colSpan={2} className="py-1 text-xs font-bold text-red-900">TOTAL RUGI (&lt; 0)</TableCell>
                          <TableCell className="py-1 text-xs font-bold text-red-700" data-testid="total-rugi-mitra">
                            {formatCurrency(filteredManagers.flatMap(m => m.accounts || []).filter(a => (a.balance || 0) < 0).reduce((sum, a) => sum + (a.balance || 0), 0))}
                          </TableCell>
                        </TableRow>
                        <TableRow className="bg-blue-100 border-t-2 border-blue-600">
                          <TableCell colSpan={2} className="py-1 text-xs font-bold text-blue-900 whitespace-nowrap">TOTAL LABA RUGI MITRA</TableCell>
                          <TableCell className="py-1 text-xs font-bold text-blue-900 whitespace-nowrap">
                            {formatCurrency(filteredManagers.flatMap(m => m.accounts || []).filter(a => showZeroBalances || a.balance !== 0).reduce((sum, a) => sum + (a.balance || 0), 0))}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </CardContent>
              </Card>
      </>
    ),
  };

  return (
    <div ref={pageRef} data-capture-root="hp" className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Hutang Piutang</h1>
        <div className="flex gap-2" data-html2canvas-ignore="true">
          <Button
            variant={reorderMode ? "default" : "outline"}
            onClick={() => setReorderMode(!reorderMode)}
            className={`gap-2 ${reorderMode ? "bg-amber-600 hover:bg-amber-700" : ""}`}
            data-testid="reorder-toggle-btn"
          >
            <ArrowUpDown size={16} /> {reorderMode ? 'Selesai Susun' : 'Susun Urutan'}
          </Button>
          <Button
            variant="outline"
            onClick={captureFullPage}
            disabled={capturing}
            className="gap-2"
            data-testid="capture-page-btn"
          >
            <Camera size={16} /> {capturing ? 'Mengambil...' : 'Capture'}
          </Button>
          {!readOnly && <Button
            variant="outline"
            onClick={() => setLayout(DEFAULT_LAYOUT.map((c) => [...c]))}
            title="Kembalikan posisi tabel ke semula"
            data-testid="hp-reset-layout-btn"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Reset Tata Letak
          </Button>}


          {/* Toggle Button for Zero Balances */}
          <Button
            variant={showZeroBalances ? "default" : "outline"}
            onClick={() => setShowZeroBalances(!showZeroBalances)}
            className={showZeroBalances ? "bg-teal-600 hover:bg-teal-700" : ""}
          >
            {showZeroBalances ? "Sembunyikan Saldo 0" : "Tampilkan Semua Rekening"}
          </Button>
          
          <Dialog open={transferDialog} onOpenChange={(open) => !transferLoading && setTransferDialog(open)}>
            {hpAccess.showTransfer && (
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700">
                <ArrowLeftRight className="mr-2 h-4 w-4" />
                Transfer
              </Button>
            </DialogTrigger>
            )}
            <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Transfer Antar Rekening</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTransfer} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm">Dari</h3>
                  <div>
                    <Label className="text-xs">Tipe</Label>
                    <SearchableSelect
                      value={transferForm.from_type}
                      onValueChange={(value) => setTransferForm({...transferForm, from_type: value, from_account_id: ''})}
                      options={[
                        { value: 'CENTRAL', label: 'Pusat' },
                        { value: 'BRANCH', label: 'Cabang' },
                        { value: 'HUTANG_USAHA', label: 'Hutang Usaha' },
                        { value: 'MANAGER', label: 'Laba Rugi Mitra' },
                        { value: 'CUSTOMER', label: 'Customer' }
                      ]}
                      placeholder="Pilih tipe..."
                      searchPlaceholder="Cari tipe..."
                      triggerClassName="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Rekening</Label>
                    <SearchableSelect
                      value={transferForm.from_account_id}
                      onValueChange={(value) => setTransferForm({...transferForm, from_account_id: value})}
                      options={getAccountsByType(transferForm.from_type, true).map(acc => ({
                        value: acc.id,
                        label: `${acc.account_name || acc.name} (${formatCurrency(acc.balance)})`
                      }))}
                      placeholder="Pilih rekening..."
                      searchPlaceholder="Cari rekening..."
                      triggerClassName="h-8 text-xs"
                    />
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm">Ke</h3>
                  <div>
                    <Label className="text-xs">Tipe</Label>
                    <SearchableSelect
                      value={transferForm.to_type}
                      onValueChange={(value) => setTransferForm({...transferForm, to_type: value, to_account_id: ''})}
                      options={[
                        { value: 'CENTRAL', label: 'Pusat' },
                        { value: 'BRANCH', label: 'Cabang' },
                        { value: 'HUTANG_USAHA', label: 'Hutang Usaha' },
                        { value: 'MANAGER', label: 'Laba Rugi Mitra' },
                        { value: 'CUSTOMER', label: 'Customer' }
                      ]}
                      placeholder="Pilih tipe..."
                      searchPlaceholder="Cari tipe..."
                      triggerClassName="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Rekening</Label>
                    <SearchableSelect
                      value={transferForm.to_account_id}
                      onValueChange={(value) => setTransferForm({...transferForm, to_account_id: value})}
                      options={getAccountsByType(transferForm.to_type, true).map(acc => ({
                        value: acc.id,
                        label: `${acc.account_name || acc.name} (${formatCurrency(acc.balance)})`
                      }))}
                      placeholder="Pilih rekening..."
                      searchPlaceholder="Cari rekening..."
                      triggerClassName="h-8 text-xs"
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs">Tanggal Transfer</Label>
                <Input
                  type="date"
                  value={transferForm.transfer_date}
                  onChange={(e) => setTransferForm({...transferForm, transfer_date: e.target.value})}
                  className="h-8 text-xs"
                  required
                  autoComplete="off"
                />
              </div>

              <div>
                <Label className="text-xs">Jumlah</Label>
                <Input
                  type="text"
                  value={formatNumber(transferForm.amount)}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\./g, '');
                    if (value === '' || /^\d+$/.test(value)) {
                      setTransferForm({...transferForm, amount: value});
                    }
                  }}
                  className="h-8 text-xs"
                  required
                  autoComplete="off"
                />
              </div>

              <div>
                <Label className="text-xs">Keterangan *</Label>
                <Input
                  value={transferForm.description}
                  onChange={(e) => setTransferForm({...transferForm, description: e.target.value})}
                  className="h-8 text-xs"
                  required
                  autoComplete="off"
                />
              </div>

              <DialogFooter>
                <Button 
                  type="submit" 
                  className="bg-teal-600 hover:bg-teal-700 text-xs h-8"
                  disabled={transferLoading}
                >
                  {transferLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Memproses...
                    </>
                  ) : (
                    'Transfer'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* 3 Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start" data-testid="hp-layout-grid">
        {/* Kolom 1 & 2 dengan Dana Pending tetap membentang persis di bawahnya */}
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 items-start content-start">
          {[0, 1].map((colIndex) => renderLayoutColumn(colIndex))}
          <div className="md:col-span-2" data-testid="hp-card-dana_pending">
            {cardRenderers.dana_pending}
          </div>
        </div>
        {[2, 3].map((colIndex) => renderLayoutColumn(colIndex))}
      </div>


      {/* Confirm Pending Dialog */}
      <Dialog open={confirmDialog} onOpenChange={setConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Konfirmasi Dana Pending</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Jumlah</Label>
              <div className="text-xl font-bold">{selectedPending ? formatCurrency(selectedPending.amount) : ''}</div>
            </div>
            <div>
              <Label className="text-sm">Masukkan ke Rekening</Label>
              <Select value={confirmForm.target_account_type} onValueChange={(value) => setConfirmForm({...confirmForm, target_account_type: value, target_account_id: ''})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CENTRAL">Rekening Pusat</SelectItem>
                  <SelectItem value="HUTANG_USAHA">Hutang Usaha</SelectItem>
                  <SelectItem value="BRANCH">Rekening Cabang</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Pilih Rekening</Label>
              <Select value={confirmForm.target_account_id} onValueChange={(value) => setConfirmForm({...confirmForm, target_account_id: value})}>
                <SelectTrigger>
                  <SelectValue  />
                </SelectTrigger>
                <SelectContent>
                  {getAccountsByType(confirmForm.target_account_type, true).map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.displayName || acc.account_name} ({formatCurrency(acc.balance || 0)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmDialog(false)}>Batal</Button>
            <Button onClick={handleConfirmPending} disabled={submitting} className="bg-teal-600 hover:bg-teal-700" data-testid="confirm-pending-submit-btn">{submitting ? 'Memproses...' : 'Konfirmasi'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Process Pending Dialog (Bulk Transfer with Checkboxes) */}
      <Dialog open={processPendingDialog} onOpenChange={(open) => {
        setProcessPendingDialog(open);
        if (!open) {
          setSelectedPendingIds([]);
          setProcessPendingForm({
            to_type: 'CENTRAL',
            to_account_id: '',
            description: ''
          });
        }
      }}>
        <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Proses Dana Pending</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* List Dana Pending dengan Checkbox */}
            <div className="border rounded-lg">
              <div className="p-3 bg-green-50 border-b flex justify-between items-center">
                <div className="text-sm font-semibold text-green-800">Pilih Dana Pending</div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="h-6 px-2 text-xs bg-teal-600 hover:bg-teal-700"
                    onClick={handleOpenAddPending}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-xs"
                    onClick={handleSelectAllPending}
                  >
                    {selectedPendingIds.length === pendingMoney.filter(p => p.status === 'PENDING').length 
                      ? 'Batal Pilih Semua' 
                      : 'Pilih Semua'}
                  </Button>
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 py-2"></TableHead>
                      <TableHead className="py-2 text-xs">Tanggal</TableHead>
                      <TableHead className="py-2 text-xs">Keterangan</TableHead>
                      <TableHead className="py-2 text-xs">Pengirim</TableHead>
                      <TableHead className="py-2 text-xs">Penerima</TableHead>
                      <TableHead className="text-right py-2 text-xs">Jumlah</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingMoney.filter(p => p.status === 'PENDING').map((money) => (
                      <TableRow 
                        key={money.id} 
                        className={`cursor-pointer hover:bg-green-50 ${selectedPendingIds.includes(money.id) ? 'bg-green-100' : ''}`}
                        onClick={() => handleTogglePendingItem(money.id)}
                      >
                        <TableCell className="py-2">
                          <input
                            type="checkbox"
                            checked={selectedPendingIds.includes(money.id)}
                            onChange={() => handleTogglePendingItem(money.id)}
                            className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell className="py-2 text-xs">
                          {new Date(money.received_date).toLocaleDateString('id-ID')}
                        </TableCell>
                        <TableCell className="py-2 text-xs">
                          {money.notes || '-'}
                        </TableCell>
                        <TableCell className="py-2 text-xs">
                          {money.jenis_transaksi === 'PENGIRIM' ? (money.rekening_name || '-') : '-'}
                        </TableCell>
                        <TableCell className="py-2 text-xs">
                          {money.jenis_transaksi === 'PENERIMA' ? (money.rekening_name || '-') : '-'}
                        </TableCell>
                        <TableCell className={`text-right py-2 text-sm font-semibold ${money.jenis_transaksi === 'PENERIMA' ? 'text-red-600' : 'text-blue-700'}`}>
                          {money.jenis_transaksi === 'PENERIMA' ? `-${formatCurrency(money.amount)}` : formatCurrency(money.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {pendingMoney.filter(p => p.status === 'PENDING').length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-slate-400 italic py-4 text-sm">
                          Tidak ada dana pending
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              {/* Total Selected */}
              {selectedPendingIds.length > 0 && (
                <div className="p-3 bg-teal-50 border-t flex justify-between items-center">
                  <div className="text-sm text-teal-700">
                    <span className="font-semibold">{selectedPendingIds.length}</span> item dipilih
                  </div>
                  <div className="text-lg font-bold text-teal-800">
                    Total: {formatCurrency(getSelectedTotalAmount())}
                  </div>
                </div>
              )}
            </div>

            {/* Rekening Tujuan */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-sm font-semibold text-blue-700 mb-2">Rekening Tujuan</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Tipe Rekening</Label>
                  <Select 
                    value={processPendingForm.to_type} 
                    onValueChange={(value) => setProcessPendingForm({...processPendingForm, to_type: value, to_account_id: ''})}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CENTRAL">Rekening Pusat</SelectItem>
                      <SelectItem value="BRANCH">Rekening Cabang</SelectItem>
                      <SelectItem value="HUTANG_USAHA">Hutang Usaha</SelectItem>
                      <SelectItem value="MANAGER">Laba Rugi Mitra</SelectItem>
                      <SelectItem value="CUSTOMER">Customer/Piutang</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Pilih Rekening</Label>
                  <Select 
                    value={processPendingForm.to_account_id} 
                    onValueChange={(value) => setProcessPendingForm({...processPendingForm, to_account_id: value})}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Pilih rekening..." />
                    </SelectTrigger>
                    <SelectContent>
                      {getAccountsByType(processPendingForm.to_type, true).map((acc) => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.displayName || acc.account_name} ({formatCurrency(acc.balance || 0)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => setProcessPendingDialog(false)}>
              Batal
            </Button>
            <Button 
              onClick={handleProcessPending} 
              className="bg-teal-600 hover:bg-teal-700"
              disabled={submitting || selectedPendingIds.length === 0 || !processPendingForm.to_account_id}
              data-testid="process-pending-submit-btn"
            >
              {submitting ? 'Memproses...' : `Proses ${selectedPendingIds.length > 0 ? `(${selectedPendingIds.length} item)` : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Pending Dialog (inside Process Dialog) */}
      <Dialog open={addPendingDialog} onOpenChange={(open) => {
        setAddPendingDialog(open);
        if (!open) {
          setAddPendingForm({
            tanggal: new Date().toISOString().split('T')[0],
            jenis_transaksi: '',
            tipe_rekening: 'CENTRAL',
            rekening_id: '',
            jumlah: '',
            keterangan: ''
          });
        }
      }}>
        <DialogContent className="max-w-md z-[150]">
          <DialogHeader>
            <DialogTitle>Tambah Dana Pending</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddPendingSubmit} className="space-y-4">
            {/* Tanggal */}
            <div>
              <Label className="text-sm">Tanggal <span className="text-red-500">*</span></Label>
              <Input
                type="date"
                className="h-9"
                value={addPendingForm.tanggal}
                onChange={(e) => setAddPendingForm({...addPendingForm, tanggal: e.target.value})}
                required
              />
            </div>

            {/* Pengirim/Penerima - Select Dropdown */}
            <div>
              <Label className="text-sm">Pengirim/Penerima <span className="text-red-500">*</span></Label>
              <Select 
                value={addPendingForm.jenis_transaksi} 
                onValueChange={(value) => setAddPendingForm({...addPendingForm, jenis_transaksi: value})}
              >
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[200]">
                  <SelectItem value="PENERIMA">Penerima</SelectItem>
                  <SelectItem value="PENGIRIM">Pengirim</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Tipe Rekening & Rekening */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Tipe Rekening <span className="text-red-500">*</span></Label>
                <Select 
                  value={addPendingForm.tipe_rekening} 
                  onValueChange={(value) => setAddPendingForm({...addPendingForm, tipe_rekening: value, rekening_id: ''})}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[200]">
                    <SelectItem value="CENTRAL">Rekening Pusat</SelectItem>
                    <SelectItem value="BRANCH">Rekening Cabang</SelectItem>
                    <SelectItem value="CUSTOMER">Piutang</SelectItem>
                    <SelectItem value="HUTANG_USAHA">Hutang Usaha</SelectItem>
                    <SelectItem value="MANAGER">Laba Rugi Mitra</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Rekening <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  value={addPendingForm.rekening_id}
                  onValueChange={(value) => setAddPendingForm({...addPendingForm, rekening_id: value})}
                  options={getAccountsByType(addPendingForm.tipe_rekening, true).map((acc) => ({
                    value: acc.id,
                    label: acc.displayName || acc.account_name
                  }))}
                  placeholder=""
                  searchPlaceholder="Ketik untuk mencari..."
                  emptyText="Tidak ditemukan"
                  triggerClassName="h-9"
                />
              </div>
            </div>
            
            {/* Jumlah */}
            <div>
              <Label className="text-sm">Jumlah (Rp) <span className="text-red-500">*</span></Label>
              <Input
                className="h-9 font-semibold"
                value={addPendingForm.jumlah}
                onChange={handleJumlahChange}
              />
            </div>

            {/* Keterangan */}
            <div>
              <Label className="text-sm">Keterangan <span className="text-red-500">*</span></Label>
              <Input
                className="h-9"
                value={addPendingForm.keterangan}
                onChange={(e) => setAddPendingForm({...addPendingForm, keterangan: e.target.value})}
              />
            </div>

            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setAddPendingDialog(false)}>
                Batal
              </Button>
              <Button 
                type="button"
                disabled={submitting}
                className="bg-teal-600 hover:bg-teal-700"
                onClick={handleAddPendingSubmit}
                data-testid="add-pending-submit-btn"
              >
                {submitting ? 'Menyimpan...' : 'Simpan'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transaction History Dialog */}
      <Dialog open={historyDialog} onOpenChange={(open) => { if (!open) exitHistoryEditMode(); setHistoryDialog(open); }}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="text-base" data-testid="history-dialog-title">
              {selectedAccount?.type === 'MANAGER'
                ? `History Laba Rugi : ${selectedAccount?.name}`
                : `History Transaksi: ${selectedAccount?.name}`}
            </DialogTitle>
          </DialogHeader>

          {canEditHistoryOrder && (
            <div className="absolute right-10 top-3 flex items-center gap-1">
              {historyEditMode ? (
                <>
                  <Button
                    size="sm"
                    className="h-6 px-2 text-[11px] bg-teal-600 hover:bg-teal-700"
                    disabled={savingOrder}
                    onClick={handleSaveHistoryOrder}
                    data-testid="history-order-save-btn"
                  >
                    {savingOrder ? 'Menyimpan...' : 'Simpan Urutan'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-[11px]"
                    onClick={exitHistoryEditMode}
                    data-testid="history-order-cancel-btn"
                  >
                    Batal
                  </Button>
                </>
              ) : (
                <Button
                  size="icon"
                  variant="outline"
                  className="h-6 w-6"
                  title="Susun ulang record (drag & drop)"
                  onClick={enterHistoryEditMode}
                  data-testid="history-edit-order-btn"
                >
                  <Pencil size={12} />
                </Button>
              )}
            </div>
          )}

          {/* View mode: Last 10 (default) or Tanggal (date range) */}
          <div className="mb-3">
            <RadioGroup
              value={historyViewMode}
              onValueChange={(val) => {
                setHistoryViewMode(val);
                if (val === 'last10') {
                  setHistoryFilter({ startDate: '', endDate: '', limit: 10 });
                }
              }}
              className="flex flex-row gap-6"
              data-testid="history-view-mode-radio"
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="last10" id="history-mode-last10" data-testid="history-mode-last10" />
                <Label htmlFor="history-mode-last10" className="text-xs cursor-pointer">10 Terakhir</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="date" id="history-mode-date" data-testid="history-mode-date" />
                <Label htmlFor="history-mode-date" className="text-xs cursor-pointer">Tanggal</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="month" id="history-mode-month" data-testid="history-mode-month" />
                <Label htmlFor="history-mode-month" className="text-xs cursor-pointer">Bulan</Label>
              </div>
              {historyViewMode === 'month' && (
                <Select value={historyMonth} onValueChange={setHistoryMonth}>
                  <SelectTrigger className="h-8 w-44 text-xs" data-testid="history-month-select">
                    <SelectValue placeholder="Pilih bulan" />
                  </SelectTrigger>
                  <SelectContent>
                    {getLast6Months().map((m) => (
                      <SelectItem key={m.value} value={m.value} className="text-xs" data-testid={`history-month-option-${m.value}`}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </RadioGroup>
          </div>

          {/* Date Filter - only visible in "Tanggal" mode */}
          {historyViewMode === 'date' && (
          <div className="grid grid-cols-3 gap-3 mb-4" data-testid="history-date-filter">
            <div>
              <Label className="text-xs">Dari Tanggal</Label>
              <Input 
                type="date" 
                className="h-8 text-xs"
                value={historyFilter.startDate}
                onChange={(e) => setHistoryFilter({...historyFilter, startDate: e.target.value})}
                data-testid="history-start-date"
              />
            </div>
            <div>
              <Label className="text-xs">Sampai Tanggal</Label>
              <Input 
                type="date" 
                className="h-8 text-xs"
                value={historyFilter.endDate}
                onChange={(e) => setHistoryFilter({...historyFilter, endDate: e.target.value})}
                data-testid="history-end-date"
              />
            </div>
            <div className="flex items-end">
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setHistoryFilter({
                  startDate: '',
                  endDate: '',
                  limit: 10
                })}
                className="h-8 text-xs"
                data-testid="history-reset-filter"
              >
                Reset Filter
              </Button>
            </div>
          </div>
          )}

          <div className="max-h-[60vh] overflow-y-auto relative [&>div]:overflow-visible">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
              </div>
            ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="h-7 py-1 text-xs bg-slate-100 sticky top-0 z-20">Tgl. Input</TableHead>
                  <TableHead
                    className="h-7 py-1 text-xs bg-slate-100 sticky top-0 z-20 cursor-pointer select-none hover:bg-slate-200"
                    onClick={toggleTxDateSort}
                    data-testid="history-sort-tgl-transaksi"
                    title="Klik untuk mengurutkan berdasarkan Tgl. Transaksi (tampilan saja)"
                  >
                    <span className="inline-flex items-center gap-1">
                      Tgl. Transaksi
                      <ArrowUpDown className="h-3 w-3" />
                      {txDateSort === 'asc' && <span className="text-teal-600">▲</span>}
                    </span>
                  </TableHead>
                  <TableHead className="h-7 py-1 text-xs bg-slate-100 sticky top-0 z-20">Tipe</TableHead>
                  <TableHead className="h-7 py-1 text-xs bg-slate-100 sticky top-0 z-20">Keterangan</TableHead>
                  <TableHead className="text-right h-7 py-1 text-xs bg-slate-100 sticky top-0 z-20">Jumlah</TableHead>
                  <TableHead className="h-7 py-1 text-xs bg-slate-100 sticky top-0 z-20">Saldo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(historyEditMode ? editRowsWithBalance() : getDisplayTransactions()).map((tx, index) => (
                  <TableRow
                    key={tx.id}
                    className={historyEditMode
                      ? [
                          'cursor-move transition-colors duration-150',
                          dragIndex === index ? 'opacity-40 [&>td]:bg-teal-50' : 'hover:[&>td]:bg-teal-50',
                          dragOverIndex === index && dragIndex !== null && dragIndex !== index
                            ? (dragIndex < index
                                ? '[&>td]:border-b-[3px] [&>td]:border-b-teal-500'
                                : '[&>td]:border-t-[3px] [&>td]:border-t-teal-500')
                            : '',
                        ].join(' ')
                      : ''}
                    draggable={historyEditMode}
                    onDragStart={() => setDragIndex(index)}
                    onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                    onDragOver={(e) => { if (historyEditMode) { e.preventDefault(); setDragOverIndex(index); } }}
                    onDragLeave={() => setDragOverIndex((prev) => (prev === index ? null : prev))}
                    onDrop={() => handleRowDrop(index)}
                    data-testid={historyEditMode ? `history-edit-row-${index}` : undefined}
                  >
                    <TableCell className="py-0.5 text-xs">
                      {historyEditMode && <GripVertical size={12} className="inline mr-1 text-slate-400" />}
                      {new Date(tx.created_at).toLocaleDateString('id-ID')}
                    </TableCell>
                    <TableCell className="py-0.5 text-xs">{tx.transaction_date ? new Date(tx.transaction_date).toLocaleDateString('id-ID') : '-'}</TableCell>
                    <TableCell className="py-0.5">
                      <Badge 
                        variant={tx.transaction_type === 'DEBIT' ? 'default' : 'destructive'} 
                        className={`text-[10px] px-1.5 py-0 ${tx.transaction_type === 'DEBIT' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                      >
                        {tx.transaction_type}
                      </Badge>
                    </TableCell>
                    <TableCell 
                      className="py-0.5 text-xs cursor-pointer hover:text-teal-600 hover:underline"
                      onClick={() => handleTransactionDetailClick(tx)}
                    >
                      {tx.description || '-'}
                    </TableCell>
                    <TableCell className={`text-right py-0.5 text-xs font-semibold whitespace-nowrap ${tx.transaction_type === 'DEBIT' ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.transaction_type === 'DEBIT' ? '+' : '-'}{formatCurrency(Math.abs(tx.amount))}
                    </TableCell>
                    <TableCell className="py-0.5 text-xs font-semibold whitespace-nowrap">{formatCurrency(tx._displayBalance)}</TableCell>
                  </TableRow>
                ))}
                {(historyEditMode ? editRows : getDisplayTransactions()).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-400 italic py-4 text-sm">Belum ada transaksi</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            )}
          </div>
          
          {/* Total Debit & Credit bulan terpilih */}
          {historyViewMode === 'month' && !loadingHistory && (() => {
            const rows = getFilteredTransactions();
            const totalDebit = rows
              .filter((tx) => tx.transaction_type === 'DEBIT')
              .reduce((sum, tx) => sum + Math.abs(tx.amount || 0), 0);
            const totalCredit = rows
              .filter((tx) => tx.transaction_type !== 'DEBIT')
              .reduce((sum, tx) => sum + Math.abs(tx.amount || 0), 0);
            const monthLabel = getLast6Months().find((m) => m.value === historyMonth)?.label || historyMonth;
            return (
              <div
                className="mt-3 flex flex-wrap items-center justify-end gap-6 rounded-md bg-slate-50 border border-slate-200 px-4 py-2"
                data-testid="history-month-totals"
              >
                <span className="text-xs text-slate-500" data-testid="history-month-totals-label">
                  {monthLabel} &middot; {rows.length} transaksi
                </span>
                <span className="text-xs font-semibold text-slate-700">
                  Total Debit:{' '}
                  <span className="text-green-600" data-testid="history-month-total-debit">{formatCurrency(totalDebit)}</span>
                </span>
                <span className="text-xs font-semibold text-slate-700">
                  Total Credit:{' '}
                  <span className="text-red-600" data-testid="history-month-total-credit">{formatCurrency(totalCredit)}</span>
                </span>
              </div>
            );
          })()}
          
          {/* Account Summary at Bottom */}
          {selectedAccount && (
            <div className="mt-4 pt-4 border-t-4 border-slate-300">
              <div className="text-right">
                <span className="text-sm font-semibold text-slate-700">
                  Sisa Saldo {selectedAccount.name} : 
                </span>
                <span className={`text-base font-bold ml-2 ${
                  getFilteredTransactions().length > 0 
                    ? (getFilteredTransactions()[getFilteredTransactions().length - 1].balance_after >= 0 ? 'text-green-600' : 'text-red-600')
                    : 'text-slate-600'
                }`}>
                  {getFilteredTransactions().length > 0 
                    ? formatCurrency(getFilteredTransactions()[getFilteredTransactions().length - 1].balance_after)
                    : 'Rp.\u00a00'}
                </span>
              </div>
              {selectedAccount.type === 'BRANCH' && branchLabaRugi !== null && (
                <div className="text-right mt-1" data-testid="branch-laba-rugi-summary">
                  <span className="text-sm font-semibold text-slate-700">
                    Saldo Laba Rugi Mitra {selectedAccount.name} : 
                  </span>
                  <span className={`text-base font-bold ml-2 ${branchLabaRugi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(branchLabaRugi)}
                  </span>
                </div>
              )}
              {selectedAccount.type === 'BRANCH' && branchInvestasi !== null && (
                <div className="text-right mt-1" data-testid="branch-investasi-summary">
                  <span className="text-sm font-semibold text-slate-700">
                    Saldo Investasi Mitra {selectedAccount.name} : 
                  </span>
                  <span className={`text-base font-bold ml-2 ${branchInvestasi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(branchInvestasi)}
                  </span>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transaction Detail Dialog */}
      <Dialog open={transactionDetailDialog} onOpenChange={setTransactionDetailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Detail Transaksi</DialogTitle>
          </DialogHeader>
          {selectedTransaction && (
            <div className="space-y-4" ref={dialogContentRef}>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Tanggal</p>
                  <p className="font-medium">
                    {selectedTransaction.transaction_date 
                      ? new Date(selectedTransaction.transaction_date).toLocaleDateString('id-ID')
                      : new Date(selectedTransaction.created_at).toLocaleDateString('id-ID')}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Tipe</p>
                  <Badge 
                    variant={selectedTransaction.transaction_type === 'DEBIT' ? 'default' : 'destructive'}
                    className={selectedTransaction.transaction_type === 'DEBIT' ? 'bg-green-600' : ''}
                  >
                    {selectedTransaction.transaction_type}
                  </Badge>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Jumlah</p>
                  <p className="font-semibold text-lg">
                    {selectedTransaction.transaction_type === 'DEBIT' ? '+' : '-'}
                    {formatCurrency(Math.abs(selectedTransaction.amount))}
                  </p>
                </div>
                <div>
                  <p className="text-slate-500 text-xs">Saldo Setelah</p>
                  <p className="font-medium">{formatCurrency(selectedTransaction.balance_after)}</p>
                </div>
              </div>

              {/* Transfer Details */}
              {selectedTransaction.transferDetails && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-sm mb-3">Detail Transfer</h4>
                  <div className="space-y-3">
                    <div className="bg-slate-50 p-3 rounded">
                      <p className="text-xs text-slate-500 mb-1">Pengirim</p>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {selectedTransaction.transferDetails.pengirim_type || selectedTransaction.transferDetails.from_type}
                        </Badge>
                        <p className="font-medium text-sm">
                          {selectedTransaction.transferDetails.pengirim_name || selectedTransaction.transferDetails.from_account_name}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex justify-center">
                      <div className="text-teal-600">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                        </svg>
                      </div>
                    </div>

                    <div className="bg-slate-50 p-3 rounded">
                      <p className="text-xs text-slate-500 mb-1">Penerima</p>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {selectedTransaction.transferDetails.penerima_type || selectedTransaction.transferDetails.to_type}
                        </Badge>
                        <p className="font-medium text-sm">
                          {selectedTransaction.transferDetails.penerima_name || selectedTransaction.transferDetails.to_account_name}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-2">
                  <p className="text-slate-500 text-xs">Keterangan</p>
                  {!editingDescription && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => setEditingDescription(true)}
                      className="h-6 text-xs text-teal-600 hover:text-teal-700"
                    >
                      Edit
                    </Button>
                  )}
                </div>
                {editingDescription ? (
                  <div className="space-y-2">
                    <Input
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      className="text-sm"
                      autoComplete="off"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={handleUpdateDescription}
                        className="bg-teal-600 hover:bg-teal-700 text-xs"
                      >
                        Simpan
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => {
                          setEditingDescription(false);
                          setEditedDescription(selectedTransaction.description || '');
                        }}
                        className="text-xs"
                      >
                        Batal
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm">{selectedTransaction.description || '-'}</p>
                )}
              </div>

              <div className="border-t pt-4">
                <p className="text-slate-500 text-xs mb-1">Jenis Transaksi</p>
                <p className="text-sm">{selectedTransaction.reference_type}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransactionDetailDialog(false)}>
              Tutup
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Konfirmasi Hapus Rekening</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Anda akan menghapus rekening: <span className="font-semibold">{deleteData.name}</span>
            </p>
            <p className="text-sm text-red-600">
              Tindakan ini tidak dapat dibatalkan. Masukkan password Anda untuk konfirmasi.
            </p>
            <form onSubmit={handleDeleteAccount} className="space-y-4">
              <div>
                <Label className="text-sm">Password *</Label>
                <Input
                  type="password"
                  required
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Masukkan password Anda"
                />
              </div>
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setDeleteDialog(false);
                    setDeletePassword('');
                    setDeleteData({ type: '', id: '', name: '' });
                  }}
                >
                  Batal
                </Button>
                <Button 
                  type="submit" 
                  variant="destructive"
                >
                  Hapus
                </Button>
              </DialogFooter>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HutangPiutang;
